// Container for all other report section tabs
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useReports } from '../ReportsProvider';
import { A4PageContainer, A4Page } from '../components/A4Page';
import SimpleRichTextEditor from '../components/SimpleRichTextEditor';
import TiptapEditor from '../components/TiptapEditor';
import TextboxPhoto from '../components/TextboxPhoto';
import { contentSplitter } from '../utils/contentSplitter';
import { API } from '../../../utils/api';
import ConfirmModal from '../../modals/ConfirmModal';
import SuccessModal from '../../modals/SuccessModal';
import ErrorModal from '../../modals/ErrorModal';

// Flag to toggle between SimpleRichTextEditor and TiptapEditor for Executive Summary
const USE_TIPTAP_FOR_EXECUTIVE_SUMMARY = true;

// Executive Summary Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const ExecutiveSummaryTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  // Initialize userTextboxes from reportSections if they exist
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.executiveSummary?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  // Photo management state
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.executiveSummary?.textboxPhotos || {};
  });
  
  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context when it changes
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('executive', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({
      ...prev,
      [editorId]: contentHeight
    }));
  };
  
  // Handle photo height updates
  const handlePhotoHeightUpdate = (textboxId, photoId, height) => {
    setPhotoHeights(prev => ({
      ...prev,
      [`${textboxId}_${photoId}`]: height
    }));
  };

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #9333ea', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>📋 2.0 Executive Summary</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#f3f4f6', 
                color: '#4b5563', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#faf5ff', 
            borderRadius: '4px', 
            border: '1px solid #e9d5ff' 
          }}>
            <p style={{ 
              color: '#7c3aed', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Overview of project status, progress metrics, achievements, and strategic priorities
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'projectOverview',
      label: 'Project Overview',
      placeholder: 'Brief overview of project status and current phase...',
      rows: 6,
      estimatedHeight: 30 // mm
    },
    {
      id: 'keyMetrics',
      label: 'Key Progress Metrics',
      placeholder: '% completion, budget status, timeline metrics...',
      rows: 5,
      estimatedHeight: 30 // mm
    },
    {
      id: 'majorAchievements',
      label: 'Major Achievements',
      placeholder: 'Significant milestones and accomplishments this period...',
      rows: 5,
      estimatedHeight: 30 // mm
    },
    {
      id: 'challengesIssues',
      label: 'Challenges & Issues',
      placeholder: 'Current challenges, risks, and mitigation strategies...',
      rows: 4,
      estimatedHeight: 30 // mm
    },
    {
      id: 'nextSteps',
      label: 'Next Steps',
      placeholder: 'Planned activities and priorities for the next period...',
      rows: 4,
      estimatedHeight: 30 // mm
    }
  ];

  // Handle adding a new textbox below the currently focused one
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextbox = {
      id: `userTextbox_${Date.now()}`,
      label: 'Custom Textbox',
      placeholder: 'Enter your content here...',
      rows: 4,
      estimatedHeight: 30,
      afterSectionId: focusedTextboxId
    };
    
    setUserTextboxes(prev => {
      const updated = [...prev, newTextbox];
      console.log(`✅ Added new textbox after ${focusedTextboxId}. Total user textboxes: ${updated.length}`);
      console.log('   User textboxes:', updated.map(tb => `${tb.id} (after: ${tb.afterSectionId})`));
      return updated;
    });
  };
  
  // Handle deleting a user-added textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('Cannot delete default textbox');
      return;
    }
    
    // Prompt for confirmation
    const hasContent = reportSections.executiveSummary?.[focusedTextboxId];
    const confirmMessage = hasContent 
      ? 'Are you sure you want to delete this textbox? Any content in it will be lost.'
      : 'Are you sure you want to delete this textbox?';
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: confirmMessage,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        performDeleteTextbox();
      }
    });
  };
  
  const performDeleteTextbox = () => {
    
    setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
    updateReportSection('executiveSummary', focusedTextboxId, '');
    setSectionHeights(prev => {
      const newHeights = { ...prev };
      delete newHeights[focusedTextboxId];
      return newHeights;
    });
    
    setFocusedTextboxId(null);
    console.log(`✅ Deleted textbox ${focusedTextboxId}`);
  };
  
  // Build ordered list of all items (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    const processedIds = new Set();
    
    // Helper function to recursively add textboxes and their children
    const addTextboxAndChildren = (textboxId) => {
      if (processedIds.has(textboxId)) return;
      processedIds.add(textboxId);
      
      // Find and add all user textboxes that come after this textbox
      const afterTextboxes = userTextboxes.filter(
        tb => tb.afterSectionId === textboxId && !processedIds.has(tb.id)
      );
      
      afterTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this user textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('executiveSummary', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('executiveSummary', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.executiveSummary?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    // Choose editor based on flag
    const EditorComponent = USE_TIPTAP_FOR_EXECUTIVE_SUMMARY ? TiptapEditor : SimpleRichTextEditor;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <EditorComponent
          value={content}
          onChange={(newContent) => {
            updateReportSection('executiveSummary', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm'
          }}
        />
      </div>
    );
  };

  // Position-based page calculation - with photos
  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like Section 2.0
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        // Get photo height
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50; // Default 50mm if not measured yet
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[]];
  };
  
  // Recalculate pages whenever section heights, user textboxes, or photos change
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Create pages with form sections - simplified approach
  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    // Safety check for pageLayout initialization
    if (!pageLayout || pageLayout.length === 0) {
      return []; // Return empty array if pageLayout not ready
    }
    
    return pageLayout.map((pageSections, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('executive', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`executive-summary-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          {/* Page Header */}
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          {/* Form Content */}
          <div style={{ 
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {pageSections.map((item, index) => {
              if (item.type === 'textbox') {
                return createFormField(item, pageIndex);
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={item.id}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScaleUp={() => handlePhotoScale(item.textboxId, item.id, 1)}
                    onScaleDown={() => handlePhotoScale(item.textboxId, item.id, -1)}
                    onMoveUp={() => handlePhotoMove(item.textboxId, item.id, -1)}
                    onMoveDown={() => handlePhotoMove(item.textboxId, item.id, 1)}
                    onHeightChange={(height) => handlePhotoHeightUpdate(item.textboxId, item.id, height)}
                    canMoveUp={false}
                    canMoveDown={false}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Executive Summary - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
    );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

// Project Overview Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const ProjectOverviewTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  // Initialize userTextboxes from reportSections if they exist
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.projectOverview?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  // Photo management state
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.projectOverview?.textboxPhotos || {};
  });
  
  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context when it changes
  useEffect(() => {
    if (updateSectionPageCount) {
      const pages = calculatePages();
      updateSectionPageCount('overview', pages.length);
    }
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Create page header component
  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #6366f1', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>🏗️ 3.0 Project Overview & Scope</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#eef2ff', 
                color: '#4338ca', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#eef2ff', 
            borderRadius: '4px', 
            border: '1px solid #c7d2fe' 
          }}>
            <p style={{ 
              color: '#4338ca', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Comprehensive overview of project scope, objectives, timeline, and key stakeholders
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'projectDescription',
      label: 'Project Description',
      placeholder: 'Provide a detailed description of the construction project, including its purpose, location, and overall goals...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'scope',
      label: 'Scope of Work',
      placeholder: 'Define specific work packages, deliverables, and boundaries of work included in this project...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'objectives',
      label: 'Project Objectives',
      placeholder: 'List primary objectives, success criteria, and key performance indicators...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'timeline',
      label: 'Project Timeline',
      placeholder: 'Outline overall timeline, major milestones, and critical path activities...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'stakeholders',
      label: 'Key Stakeholders',
      placeholder: 'Identify client representatives, contractors, suppliers, consultants, and other key parties involved...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'constraints',
      label: 'Constraints & Assumptions',
      placeholder: 'Document key constraints, limitations, and assumptions that impact project delivery...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'qualityStandards',
      label: 'Quality Standards & Requirements',
      placeholder: 'Specify applicable building codes, industry standards, and quality requirements...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('projectOverview', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  const handlePhotoHeightUpdate = (textboxId, photoId, height) => {
    setPhotoHeights(prev => ({
      ...prev,
      [`${textboxId}_${photoId}`]: height
    }));
  };
  
  // Get all items in order (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Build ordered list of all textboxes (default + user-added) - for backward compatibility
  const getAllTextboxes = () => {
    const allTextboxes = [];
    const processedIds = new Set();
    
    // Helper function to recursively add textboxes and their children
    const addTextboxAndChildren = (textboxId) => {
      if (processedIds.has(textboxId)) return;
      processedIds.add(textboxId);
      
      const afterTextboxes = userTextboxes.filter(
        tb => tb.parentId === textboxId && !processedIds.has(tb.id)
      );
      
      afterTextboxes.forEach(tb => {
        allTextboxes.push(tb);
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      allTextboxes.push(section);
      addTextboxAndChildren(section.id);
    });
    
    return allTextboxes;
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('projectOverview', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('projectOverview', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle focus change
  const handleFocusChange = (isFocused, textboxId) => {
    if (onRichTextFocusChange) {
      onRichTextFocusChange(isFocused);
    }
    
    if (isFocused) {
      setFocusedTextboxId(textboxId);
    }
  };

  const createFormField = (section, pageIndex) => {
    const content = reportSections.projectOverview?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('projectOverview', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => handleFocusChange(isFocused, section.id)}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm'
          }}
        />
      </div>
    );
  };

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like Section 2.0
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[]];
  };
  
  // Recalculate pages whenever section heights, user textboxes, or photos change
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  const createPages = () => {
    const pages = calculatePages();
    const localTotalPages = pages.length;
    
    return pages.map((pageSections, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('overview', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`project-overview-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageSections.map((item, index) => {
              if (item.type === 'textbox') {
                return createFormField(item, pageIndex);
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={item.id}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScaleUp={() => handlePhotoScale(item.textboxId, item.id, 1)}
                    onScaleDown={() => handlePhotoScale(item.textboxId, item.id, -1)}
                    onMoveUp={() => handlePhotoMove(item.textboxId, item.id, -1)}
                    onMoveDown={() => handlePhotoMove(item.textboxId, item.id, 1)}
                    onHeightChange={(height) => handlePhotoHeightUpdate(item.textboxId, item.id, height)}
                    canMoveUp={false}
                    canMoveDown={false}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Project Overview & Scope - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
    );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Progress Highlights Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const ProgressHighlightsTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  // Initialize userTextboxes from reportSections if they exist
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.progressHighlights?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  // Photo management state
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.progressHighlights?.textboxPhotos || {};
  });
  
  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context when it changes
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('progress', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);
  
  // Add custom styles for range input
  React.useEffect(() => {
    const styleId = 'progress-highlights-slider-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #eab308;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #eab308;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input[type="range"]::-webkit-slider-runnable-track {
          height: 8px;
          border-radius: 4px;
        }
        input[type="range"]::-moz-range-track {
          height: 8px;
          border-radius: 4px;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Textbox management handlers
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextbox = {
      id: `userTextbox_${Date.now()}`,
      label: 'Custom Textbox',
      placeholder: 'Enter your content here...',
      rows: 4,
      estimatedHeight: 30,
      afterSectionId: focusedTextboxId
    };
    
    setUserTextboxes(prev => {
      const updated = [...prev, newTextbox];
      console.log(`✅ Added new textbox after ${focusedTextboxId}. Total user textboxes: ${updated.length}`);
      return updated;
    });
  };
  
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('Cannot delete default textbox');
      return;
    }
    
    const hasContent = reportSections.progressHighlights?.[focusedTextboxId];
    const confirmMessage = hasContent 
      ? 'Are you sure you want to delete this textbox? Any content in it will be lost.'
      : 'Are you sure you want to delete this textbox?';
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: confirmMessage,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        updateReportSection('progressHighlights', focusedTextboxId, '');
        setSectionHeights(prev => {
          const newHeights = { ...prev };
          delete newHeights[focusedTextboxId];
          return newHeights;
        });
        
        setFocusedTextboxId(null);
        console.log(`✅ Deleted textbox ${focusedTextboxId}`);
      }
    });
  };
  
  // Photo management handlers
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50,
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  const handlePhotoHeightUpdate = (textboxId, photoId, height) => {
    setPhotoHeights(prev => ({
      ...prev,
      [`${textboxId}_${photoId}`]: height
    }));
  };
  
  // Height tracking
  const handleHeightUpdate = ({ editorId, contentHeight }) => {
    setSectionHeights(prev => ({
      ...prev,
      [editorId]: contentHeight
    }));
  };
  
  const handleFocusChange = (isFocused, textboxId) => {
    if (onRichTextFocusChange) {
      onRichTextFocusChange(isFocused);
    }
    
    if (isFocused) {
      setFocusedTextboxId(textboxId);
    }
  };
  
  // Build ordered list of all items
  const getAllItems = () => {
    const allItems = [];
    const processedIds = new Set();
    
    const addTextboxAndChildren = (textboxId) => {
      if (processedIds.has(textboxId)) return;
      processedIds.add(textboxId);
      
      const afterTextboxes = userTextboxes.filter(
        tb => tb.afterSectionId === textboxId && !processedIds.has(tb.id)
      );
      
      afterTextboxes.forEach(tb => {
        allItems.push({ type: 'textbox', ...tb });
        
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        addTextboxAndChildren(tb.id);
      });
    };
    
    formSections.forEach(section => {
      allItems.push({ type: 'textbox', ...section });
      
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Save state
  useEffect(() => {
    updateReportSection('progressHighlights', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('progressHighlights', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Register handlers
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #eab308', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>🌟 4.0 Progress Highlights</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#fefce8', 
                color: '#a16207', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#fefce8', 
            borderRadius: '4px', 
            border: '1px solid #fde047' 
          }}>
            <p style={{ 
              color: '#a16207', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Key achievements, milestones reached, and significant progress made during this reporting period
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'majorMilestones',
      label: 'Major Milestones Achieved',
      placeholder: 'List significant milestones completed during this period, including completion dates and impact on overall project timeline...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'notableAchievements',
      label: 'Notable Achievements & Successes',
      placeholder: 'Highlight exceptional performance, innovative solutions, early completions, cost savings, or other notable successes...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'performanceImprovements',
      label: 'Performance Improvements & Innovations',
      placeholder: 'Document process improvements, technology adoptions, efficiency gains, or innovative approaches implemented...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'teamRecognition',
      label: 'Team Recognition & Outstanding Contributions',
      placeholder: 'Acknowledge exceptional team performance, individual contributions, safety records, or collaborative efforts...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  const createFormField = (section, pageIndex) => {
    const content = reportSections.progressHighlights?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('progressHighlights', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => handleFocusChange(isFocused, section.id)}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm'
          }}
        />
      </div>
    );
  };

  // Create KPI section with sliders
  const createKPISection = () => {
    const overallProgress = parseInt(reportSections.progressHighlights?.overallProgress || 0);
    const programme = reportSections.progressHighlights?.programme || 'ontime';
    
    return (
      <div style={{ marginBottom: '6mm' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '600', 
          color: '#111827', 
          marginBottom: '3mm' 
        }}>
          Key Performance Indicators
        </label>
        <div style={{ 
          backgroundColor: '#fefce8',
          padding: '6mm',
          borderRadius: '2px',
          border: '1px solid #fde047',
          marginBottom: '3mm'
        }}>
          {/* Overall Progress Slider */}
          <div style={{ marginBottom: '5mm' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#92400e', 
              marginBottom: '2mm' 
            }}>
              Overall Progress: <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{overallProgress}%</span>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={overallProgress}
              onChange={(e) => updateReportSection('progressHighlights', 'overallProgress', e.target.value)}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: `linear-gradient(to right, #eab308 0%, #eab308 ${overallProgress}%, #d1d5db ${overallProgress}%, #d1d5db 100%)`,
                outline: 'none',
                WebkitAppearance: 'none',
                appearance: 'none',
                cursor: 'pointer'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1mm' }}>
              <span style={{ fontSize: '10px', color: '#6b7280' }}>0%</span>
              <span style={{ fontSize: '10px', color: '#6b7280' }}>100%</span>
            </div>
          </div>
          
          {/* Programme Slider (3-point snap) */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#92400e', 
              marginBottom: '2mm' 
            }}>
              Programme: <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {programme === 'delayed' ? 'Delayed' : programme === 'ontime' ? 'On Time' : 'Ahead'}
              </span>
            </label>
            <div style={{ position: 'relative', paddingBottom: '2mm' }}>
              <input
                type="range"
                min="0"
                max="2"
                step="1"
                value={programme === 'delayed' ? 0 : programme === 'ontime' ? 1 : 2}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  const newProgramme = val === 0 ? 'delayed' : val === 1 ? 'ontime' : 'ahead';
                  updateReportSection('progressHighlights', 'programme', newProgramme);
                }}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  background: '#d1d5db',
                  outline: 'none',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  cursor: 'pointer'
                }}
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginTop: '2mm',
                paddingLeft: '2mm',
                paddingRight: '2mm'
              }}>
                <span style={{ 
                  fontSize: '11px', 
                  fontWeight: programme === 'delayed' ? '700' : '500',
                  color: programme === 'delayed' ? '#dc2626' : '#6b7280'
                }}>Delayed</span>
                <span style={{ 
                  fontSize: '11px', 
                  fontWeight: programme === 'ontime' ? '700' : '500',
                  color: programme === 'ontime' ? '#6b7280' : '#9ca3af'
                }}>On Time</span>
                <span style={{ 
                  fontSize: '11px', 
                  fontWeight: programme === 'ahead' ? '700' : '500',
                  color: programme === 'ahead' ? '#059669' : '#6b7280'
                }}>Ahead</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    const KPI_HEIGHT = 80; // Fixed height for KPI section
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [{ type: 'kpi', itemHeight: KPI_HEIGHT }];
    let currentPageUsedHeight = KPI_HEIGHT;
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'kpi', itemHeight: KPI_HEIGHT }]];
  };
  
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  const createPages = () => {
    const pages = calculatePages();
    const localTotalPages = pages.length;
    
    return pages.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('progress', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`progress-highlights-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, index) => {
              if (item.type === 'kpi') {
                return <div key="kpi">{createKPISection()}</div>;
              } else if (item.type === 'textbox') {
                return createFormField(item, pageIndex);
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={item.id}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScaleUp={() => handlePhotoScale(item.textboxId, item.id, 1)}
                    onScaleDown={() => handlePhotoScale(item.textboxId, item.id, -1)}
                    onMoveUp={() => handlePhotoMove(item.textboxId, item.id, -1)}
                    onMoveDown={() => handlePhotoMove(item.textboxId, item.id, 1)}
                    onHeightChange={(height) => handlePhotoHeightUpdate(item.textboxId, item.id, height)}
                    canMoveUp={false}
                    canMoveDown={false}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Progress Highlights - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
    );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Work Completed This Period Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const WorkCompletedTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.workCompleted?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.workCompleted?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context when it changes
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('work', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #10b981', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>✅ 5.0 Work Completed This Period</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#ecfdf5', 
                color: '#065f46', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#ecfdf5', 
            borderRadius: '4px', 
            border: '1px solid #86efac' 
          }}>
            <p style={{ 
              color: '#065f46', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Detailed breakdown of all construction activities and deliverables completed during this reporting period
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'structuralWork',
      label: 'Structural Work Completed',
      placeholder: 'Detail foundation work, framing, concrete pours, steel erection, structural modifications, or other structural activities completed...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'mepSystems',
      label: 'MEP Systems Installation',
      placeholder: 'Document electrical installations, plumbing rough-in, HVAC systems, fire protection, communications, or MEP testing completed...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'architecturalWork',
      label: 'Architectural Work & Finishes',
      placeholder: 'Report on drywall, flooring, painting, millwork, doors, windows, roofing, exterior cladding, or interior finishes completed...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'siteWork',
      label: 'Site Work & External Utilities',
      placeholder: 'Document site preparation, excavation, utilities connection, paving, landscaping, parking areas, or site improvements completed...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'testingCommissioning',
      label: 'Testing & Commissioning Activities',
      placeholder: 'Report system testing, equipment commissioning, performance verification, punch list items, or final inspections completed...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Handlers
  const handleAddTextbox = () => {
    if (!focusedTextboxId) return;
    const newTextbox = {
      id: `userTextbox_${Date.now()}`,
      label: 'Custom Textbox',
      placeholder: 'Enter your content here...',
      rows: 4,
      estimatedHeight: 30,
      afterSectionId: focusedTextboxId
    };
    setUserTextboxes(prev => [...prev, newTextbox]);
  };
  
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) return;
    const hasContent = reportSections.workCompleted?.[focusedTextboxId];
    const message = hasContent ? 'Are you sure you want to delete this textbox? Any content in it will be lost.' : 'Are you sure you want to delete this textbox?';
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: message,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        updateReportSection('workCompleted', focusedTextboxId, '');
        setSectionHeights(prev => {
          const newHeights = { ...prev };
          delete newHeights[focusedTextboxId];
          return newHeights;
        });
        setFocusedTextboxId(null);
      }
    });
  };
  
  const handleAddPhotos = () => {
    if (!focusedTextboxId) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      Promise.all(files.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve({
          id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          url: e.target.result,
          name: file.name,
          scale: 50,
          textboxId: focusedTextboxId
        });
        reader.readAsDataURL(file);
      }))).then(newPhotos => {
        setTextboxPhotos(prev => ({
          ...prev,
          [focusedTextboxId]: [...(prev[focusedTextboxId] || []), ...newPhotos]
        }));
      });
    };
    input.click();
  };
  
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) } : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1 || (index + direction < 0) || (index + direction >= photos.length)) return prev;
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[index + direction]] = [newPhotos[index + direction], newPhotos[index]];
      return { ...prev, [textboxId]: newPhotos };
    });
  };
  
  const handlePhotoHeightUpdate = (textboxId, photoId, height) => {
    setPhotoHeights(prev => ({ ...prev, [`${textboxId}_${photoId}`]: height }));
  };
  
  const handleHeightUpdate = ({ editorId, contentHeight }) => {
    setSectionHeights(prev => ({ ...prev, [editorId]: contentHeight }));
  };
  
  const handleFocusChange = (isFocused, textboxId) => {
    if (onRichTextFocusChange) onRichTextFocusChange(isFocused);
    if (isFocused) setFocusedTextboxId(textboxId);
  };
  
  const getAllItems = () => {
    const allItems = [];
    const processedIds = new Set();
    const addTextboxAndChildren = (textboxId) => {
      if (processedIds.has(textboxId)) return;
      processedIds.add(textboxId);
      userTextboxes.filter(tb => tb.afterSectionId === textboxId && !processedIds.has(tb.id)).forEach(tb => {
        allItems.push({ type: 'textbox', ...tb });
        (textboxPhotos[tb.id] || []).forEach(photo => allItems.push({ type: 'photo', ...photo, textboxId: tb.id }));
        addTextboxAndChildren(tb.id);
      });
    };
    formSections.forEach(section => {
      allItems.push({ type: 'textbox', ...section });
      (textboxPhotos[section.id] || []).forEach(photo => allItems.push({ type: 'photo', ...photo, textboxId: section.id }));
      addTextboxAndChildren(section.id);
    });
    return allItems;
  };
  
  useEffect(() => {
    updateReportSection('workCompleted', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('workCompleted', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);
  
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = { onAdd: handleAddTextbox, onDelete: handleDeleteTextbox, onAddPhotos: handleAddPhotos };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  useEffect(() => {
    if (onTextboxInfoChange) {
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: focusedTextboxId && focusedTextboxId.startsWith('userTextbox_')
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  const createFormField = (section, pageIndex) => {
    const content = reportSections.workCompleted?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    const PAGE_HEIGHT = 277, HEADER_HEIGHT = 25, FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8, SECTION_MARGIN = 5;
    
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        usedHeightBefore += currentPageItems[i].itemHeight || 25;
      }
    }
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => updateReportSection('workCompleted', section.id, newContent)}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => handleFocusChange(isFocused, section.id)}
          pageContext={{ pageIndex, editorId: section.id, maxHeight }}
          style={{ width: '190mm' }}
        />
      </div>
    );
  };

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like Section 2.0
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[]];
  };
  
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  const createPages = () => {
    const pages = calculatePages();
    const localTotalPages = pages.length;
    
    return pages.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('work', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`work-completed-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ flex: 1, paddingBottom: '10mm', overflowY: 'hidden' }}>
            {pageItems.map((item, index) => {
              if (item.type === 'textbox') {
                return createFormField(item, pageIndex);
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={item.id}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScaleUp={() => handlePhotoScale(item.textboxId, item.id, 1)}
                    onScaleDown={() => handlePhotoScale(item.textboxId, item.id, -1)}
                    onMoveUp={() => handlePhotoMove(item.textboxId, item.id, -1)}
                    onMoveDown={() => handlePhotoMove(item.textboxId, item.id, 1)}
                    onHeightChange={(height) => handlePhotoHeightUpdate(item.textboxId, item.id, height)}
                    canMoveUp={false}
                    canMoveDown={false}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Work Completed This Period - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
    );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};
// Quality Inspections & Standards Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const QualityInspectionsTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.qualityInspections?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.qualityInspections?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context when it changes
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('quality', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('qualityInspections', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Get all items in order (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('qualityInspections', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('qualityInspections', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Recalculate pages whenever section heights, user textboxes, or photos change  
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  // Enhanced metrics calculation functions
  const handleMetricsChange = (fieldId, value) => {
    const totalInspections = fieldId === 'totalInspections' ? parseInt(value) || 0 : parseInt(reportSections.qualityInspections?.totalInspections) || 0;
    const inspectionsPassed = fieldId === 'inspectionsPassed' ? parseInt(value) || 0 : parseInt(reportSections.qualityInspections?.inspectionsPassed) || 0;
    
    // Validate that passed cannot be higher than total
    if (fieldId === 'inspectionsPassed' && parseInt(value) > totalInspections) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid Value',
        message: 'Passed inspections cannot be higher than total inspections.'
      });
      return;
    }
    
    // Update the input field
    updateReportSection('qualityInspections', fieldId, value);
    
    // Calculate and update derived values
    if (fieldId === 'totalInspections' || fieldId === 'inspectionsPassed') {
      const adjustedPassed = fieldId === 'totalInspections' && inspectionsPassed > parseInt(value) 
        ? parseInt(value) 
        : (fieldId === 'inspectionsPassed' ? parseInt(value) || 0 : inspectionsPassed);
      
      const adjustedTotal = fieldId === 'totalInspections' ? parseInt(value) || 0 : totalInspections;
      
      // If passed was reduced due to total being reduced, update it
      if (fieldId === 'totalInspections' && inspectionsPassed > parseInt(value)) {
        updateReportSection('qualityInspections', 'inspectionsPassed', adjustedPassed.toString());
      }
      
      // Calculate failed inspections
      const inspectionsFailed = Math.max(0, adjustedTotal - adjustedPassed);
      updateReportSection('qualityInspections', 'inspectionsFailed', inspectionsFailed.toString());
      
      // Calculate success rate
      const successRate = adjustedTotal > 0 ? ((adjustedPassed / adjustedTotal) * 100).toFixed(1) : '0';
      updateReportSection('qualityInspections', 'successRate', successRate);
    }
  };

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #3b82f6', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>🔍 6.0 Quality Inspections & Standards</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#eff6ff', 
                color: '#1e40af', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#eff6ff', 
            borderRadius: '4px', 
            border: '1px solid #93c5fd' 
          }}>
            <p style={{ 
              color: '#1e40af', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Quality control measures, inspection results, standards compliance, and corrective actions taken
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'overview',
      label: 'Quality Control Overview',
      placeholder: 'Provide overview of quality management approach, standards followed, and overall quality performance during this period...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'inspectionDetails',
      label: 'Detailed Inspection Results',
      placeholder: 'Detail specific inspections conducted, including structural inspections, MEP inspections, material testing, code compliance checks, third-party inspections...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'standardsCompliance',
      label: 'Standards & Code Compliance',
      placeholder: 'Document compliance with building codes, industry standards (ASTM, ACI, AISC), project specifications, and regulatory requirements...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'nonConformances',
      label: 'Non-Conformances & Corrective Actions',
      placeholder: 'List quality issues identified, root causes, corrective actions taken, preventive measures implemented, and lessons learned...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'materialTesting',
      label: 'Material Testing & Verification',
      placeholder: 'Report on concrete testing, steel certifications, material approvals, laboratory test results, and quality certificates received...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'qualityImprovements',
      label: 'Quality Improvement Initiatives',
      placeholder: 'Document quality management improvements, process enhancements, training programs, or new quality control measures implemented...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.qualityInspections?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('qualityInspections', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        />
      </div>
    );
  };

  const createInspectionMetrics = () => (
    <div style={{ marginBottom: '6mm' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '3mm' 
      }}>
        Inspection Performance Metrics
      </label>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr 1fr 1fr', 
        gap: '3mm',
        backgroundColor: '#eff6ff',
        padding: '4mm',
        borderRadius: '2px',
        border: '1px solid #93c5fd',
        marginBottom: '3mm'
      }}>
        {[
          { id: 'totalInspections', label: 'Total Inspections', placeholder: '0', editable: true },
          { id: 'inspectionsPassed', label: 'Passed', placeholder: '0', editable: true },
          { id: 'inspectionsFailed', label: 'Failed', placeholder: '0', editable: false },
          { id: 'successRate', label: 'Success Rate (%)', placeholder: '0', editable: false }
        ].map(field => (
          <div key={field.id}>
            <label style={{ 
              display: 'block', 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#1e40af', 
              marginBottom: '1mm' 
            }}>
              {field.label}
            </label>
            <input
              type="number"
              min="0"
              step={field.id === 'successRate' ? '0.1' : '1'}
              value={reportSections.qualityInspections?.[field.id] || (field.editable ? '' : '0')}
              onChange={(e) => field.editable ? handleMetricsChange(field.id, e.target.value) : null}
              readOnly={!field.editable}
              style={{
                width: '100%',
                padding: '2mm',
                border: '1px solid #d1d5db',
                borderRadius: '2px',
                fontSize: '11px',
                outline: 'none',
                backgroundColor: field.editable ? 'white' : '#f9fafb',
                cursor: field.editable ? 'text' : 'not-allowed'
              }}
              placeholder={field.placeholder}
              onFocus={(e) => {
                if (field.editable) {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 1px #3b82f6';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    const METRICS_HEIGHT = 25;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like Section 2.0
    
    // Add metrics as first item manually
    const metricsItem = { type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT };
    if (currentPageUsedHeight + METRICS_HEIGHT <= AVAILABLE_HEIGHT) {
      currentPage.push(metricsItem);
      currentPageUsedHeight += METRICS_HEIGHT;
    }
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT }]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    return pageLayout.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('quality', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`quality-inspections-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, itemIndex) => {
              if (item.type === 'metrics') {
                return createInspectionMetrics();
              } else if (item.type === 'textbox') {
                const section = item.id.startsWith('userTextbox_') 
                  ? userTextboxes.find(tb => tb.id === item.id)
                  : formSections.find(s => s.id === item.id);
                return section ? createFormField(section, pageIndex) : null;
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={`${item.id}-${pageIndex}-${itemIndex}`}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScale={(direction) => handlePhotoScale(item.textboxId, item.id, direction)}
                    onMove={(direction) => handlePhotoMove(item.textboxId, item.id, direction)}
                    onHeightChange={(height) => {
                      setPhotoHeights(prev => ({ ...prev, [item.id]: height }));
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Quality Inspections & Standards - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
    );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Safety Compliance & Incidents Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const SafetyComplianceTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.safetyCompliance?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.safetyCompliance?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('safety', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('safetyCompliance', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Get all items in order (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  const handlePhotoHeightUpdate = (textboxId, photoId, height) => {
    setPhotoHeights(prev => ({
      ...prev,
      [`${textboxId}_${photoId}`]: height
    }));
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('safetyCompliance', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('safetyCompliance', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Recalculate pages whenever section heights, user textboxes, or photos change  
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #dc2626', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>🛡️ 7.0 Safety Compliance & Incidents</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#fef2f2', 
                color: '#991b1b', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#fef2f2', 
            borderRadius: '4px', 
            border: '1px solid #fca5a5' 
          }}>
            <p style={{ 
              color: '#991b1b', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Safety performance metrics, incident reports, compliance status, and safety improvement initiatives
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'incidentReports',
      label: 'Incident Reports & Analysis',
      placeholder: 'Document any safety incidents, injuries, near misses, property damage, or unsafe conditions identified. Include dates, locations, root causes, and immediate actions taken...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'complianceStatus',
      label: 'Regulatory Compliance Status',
      placeholder: 'Report compliance with OSHA regulations, local safety codes, project safety requirements, permit conditions, and safety plan adherence...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'safetyInspections',
      label: 'Safety Inspections & Audits',
      placeholder: 'Detail safety inspections conducted, audit results, compliance findings, violations identified, and corrective actions implemented...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'ppeCompliance',
      label: 'PPE Compliance & Equipment Status',
      placeholder: 'Report on PPE usage compliance, equipment condition, new PPE issued, training provided, and any PPE-related incidents...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'safetyTraining',
      label: 'Safety Training & Communication',
      placeholder: 'Document safety training sessions conducted, toolbox talks, safety communications, emergency drills, and worker engagement initiatives...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'safetyImprovements',
      label: 'Safety Improvement Initiatives',
      placeholder: 'Report on new safety procedures implemented, safety equipment upgrades, process improvements, or proactive safety measures introduced...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.safetyCompliance?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('safetyCompliance', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        />
      </div>
    );
  };

  const createSafetyMetrics = () => (
    <div style={{ marginBottom: '6mm' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '3mm' 
      }}>
        Safety Performance Metrics
      </label>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr 1fr 1fr', 
        gap: '3mm',
        backgroundColor: '#fef2f2',
        padding: '4mm',
        borderRadius: '2px',
        border: '1px solid #fca5a5',
        marginBottom: '3mm'
      }}>
        {[
          { id: 'daysWithoutIncident', label: 'Days Without Incident', placeholder: '0' },
          { id: 'totalLaborHours', label: 'Total Labor Hours', placeholder: '0' },
          { id: 'nearMisses', label: 'Near Misses Reported', placeholder: '0' },
          { id: 'trainingHours', label: 'Safety Training Hours', placeholder: '0' }
        ].map(field => (
          <div key={field.id}>
            <label style={{ 
              display: 'block', 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#991b1b', 
              marginBottom: '1mm' 
            }}>
              {field.label}
            </label>
            <input
              type="number"
              value={reportSections.safetyCompliance?.[field.id] || ''}
              onChange={(e) => updateReportSection('safetyCompliance', field.id, e.target.value)}
              style={{
                width: '100%',
                padding: '2mm',
                border: '1px solid #d1d5db',
                borderRadius: '2px',
                fontSize: '11px',
                outline: 'none'
              }}
              placeholder={field.placeholder}
              onFocus={(e) => {
                e.target.style.borderColor = '#dc2626';
                e.target.style.boxShadow = '0 0 0 1px #dc2626';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    const METRICS_HEIGHT = 25;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like Section 2.0
    
    // Add metrics as first item manually
    const metricsItem = { type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT };
    if (currentPageUsedHeight + METRICS_HEIGHT <= AVAILABLE_HEIGHT) {
      currentPage.push(metricsItem);
      currentPageUsedHeight += METRICS_HEIGHT;
    }
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT }]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    return pageLayout.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('safety', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`safety-compliance-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, itemIndex) => {
              if (item.type === 'metrics') {
                return createSafetyMetrics();
              } else if (item.type === 'textbox') {
                const section = item.id.startsWith('userTextbox_') 
                  ? userTextboxes.find(tb => tb.id === item.id)
                  : formSections.find(s => s.id === item.id);
                return section ? createFormField(section, pageIndex) : null;
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={`${item.id}-${pageIndex}-${itemIndex}`}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScale={(direction) => handlePhotoScale(item.textboxId, item.id, direction)}
                    onMove={(direction) => handlePhotoMove(item.textboxId, item.id, direction)}
                    onHeightChange={(height) => {
                      setPhotoHeights(prev => ({ ...prev, [item.id]: height }));
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Safety Compliance & Incidents - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};
// Schedule Performance Analysis Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const SchedulePerformanceTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.schedulePerformance?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.schedulePerformance?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('schedule', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('schedulePerformance', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Get all items in order (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('schedulePerformance', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('schedulePerformance', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Recalculate pages whenever section heights, user textboxes, or photos change  
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  // Enhanced metrics calculation functions
  const handleMetricsChange = (fieldId, value) => {
    const originalDuration = fieldId === 'originalDuration' ? parseInt(value) || 0 : parseInt(reportSections.schedulePerformance?.originalDuration) || 0;
    const revisedDuration = fieldId === 'revisedDuration' ? parseInt(value) || 0 : parseInt(reportSections.schedulePerformance?.revisedDuration) || 0;
    
    // Update the input field
    updateReportSection('schedulePerformance', fieldId, value);
    
    // Calculate and update SPI (Schedule Performance Index)
    if (fieldId === 'originalDuration' || fieldId === 'revisedDuration') {
      const adjustedOriginal = fieldId === 'originalDuration' ? parseInt(value) || 0 : originalDuration;
      const adjustedRevised = fieldId === 'revisedDuration' ? parseInt(value) || 0 : revisedDuration;
      
      // Calculate SPI = Original Duration / Revised Duration (a value > 1 means ahead of schedule, < 1 means behind)
      const spi = adjustedRevised > 0 ? (adjustedOriginal / adjustedRevised).toFixed(2) : '0.00';
      updateReportSection('schedulePerformance', 'spi', spi);
    }
  };

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #6366f1', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>⏱️ 8.0 Schedule Performance Analysis</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#eef2ff', 
                color: '#4338ca', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#eef2ff', 
            borderRadius: '4px', 
            border: '1px solid #c7d2fe' 
          }}>
            <p style={{ 
              color: '#4338ca', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Comprehensive analysis of project schedule performance, timeline adherence, and critical path management
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'criticalPath',
      label: 'Critical Path Analysis',
      placeholder: 'Identify current critical path activities, float analysis, potential bottlenecks, and activities that could impact project completion date...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'scheduleVariances',
      label: 'Schedule Variances & Delays',
      placeholder: 'Document schedule variances, delays encountered, root causes, impact analysis, and recovery plans implemented...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'milestonePerformance',
      label: 'Milestone Performance Tracking',
      placeholder: 'Track key milestone achievements, planned vs actual completion dates, milestone delays, and corrective actions taken...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'resourceImpact',
      label: 'Resource Allocation Impact on Schedule',
      placeholder: 'Analyze how resource availability, allocation changes, and productivity affected schedule performance...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'cashflowImpact',
      label: 'Cashflow Impact on Schedule',
      placeholder: 'Analyze how cash flow constraints, payment timing, budget availability, and financial milestones have impacted project schedule and delivery timelines...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'recoveryStrategies',
      label: 'Schedule Recovery Strategies',
      placeholder: 'Detail recovery strategies implemented, fast-tracking measures, crashing activities, and their effectiveness...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.schedulePerformance?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('schedulePerformance', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        />
      </div>
    );
  };

  const createScheduleMetrics = () => (
    <div style={{ marginBottom: '6mm' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '3mm' 
      }}>
        Overall Schedule Performance
      </label>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr 1fr', 
        gap: '3mm',
        backgroundColor: '#eef2ff',
        padding: '4mm',
        borderRadius: '2px',
        border: '1px solid #c7d2fe',
        marginBottom: '3mm'
      }}>
        {[
          { id: 'originalDuration', label: 'Original Duration (Days)', placeholder: '0', editable: true },
          { id: 'revisedDuration', label: 'Revised Duration (Days)', placeholder: '0', editable: true },
          { id: 'spi', label: 'Schedule Performance Index', placeholder: '0.00', step: '0.01', editable: false, hasTooltip: true }
        ].map(field => (
          <div key={field.id}>
            <label style={{ 
              display: 'block', 
              fontSize: '11px', 
              fontWeight: '500', 
              color: '#4338ca', 
              marginBottom: '1mm' 
            }}>
              {field.label}
              {field.hasTooltip && (
                <span 
                  title="Schedule Performance Index (SPI) = Original Duration ÷ Revised Duration&#10;&#10;Interpretation:&#10;• SPI > 1.0 = Ahead of schedule (GOOD)&#10;• SPI = 1.0 = On schedule&#10;• SPI < 1.0 = Behind schedule (ATTENTION NEEDED)&#10;&#10;Example: If original duration was 100 days and revised is 120 days, SPI = 0.83 (behind schedule)"
                  style={{ 
                    marginLeft: '2mm', 
                    cursor: 'help',
                    color: '#6366f1',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}
                >
                  ℹ️
                </span>
              )}
            </label>
            <input
              type="number"
              min="0"
              step={field.step || '1'}
              value={reportSections.schedulePerformance?.[field.id] || (field.editable ? '' : '0.00')}
              onChange={(e) => field.editable ? handleMetricsChange(field.id, e.target.value) : null}
              readOnly={!field.editable}
              style={{
                width: '100%',
                padding: '2mm',
                border: '1px solid #d1d5db',
                borderRadius: '2px',
                fontSize: '11px',
                outline: 'none',
                backgroundColor: field.editable ? 'white' : '#f9fafb',
                cursor: field.editable ? 'text' : 'not-allowed'
              }}
              placeholder={field.placeholder}
              onFocus={(e) => {
                if (field.editable) {
                  e.target.style.borderColor = '#6366f1';
                  e.target.style.boxShadow = '0 0 0 1px #6366f1';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    const METRICS_HEIGHT = 25;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like Section 2.0
    
    // Add metrics as first item manually
    const metricsItem = { type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT };
    if (currentPageUsedHeight + METRICS_HEIGHT <= AVAILABLE_HEIGHT) {
      currentPage.push(metricsItem);
      currentPageUsedHeight += METRICS_HEIGHT;
    }
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT }]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    // Fallback to calculatePages if pageLayout is empty (initial render)
    const pagesData = localTotalPages > 0 ? pageLayout : calculatePages();
    const actualTotalPages = pagesData.length;
    
    return pagesData.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('schedule', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`schedule-performance-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, actualTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, itemIndex) => {
              if (item.type === 'metrics') {
                return createScheduleMetrics();
              } else if (item.type === 'textbox') {
                const section = item.id.startsWith('userTextbox_') 
                  ? userTextboxes.find(tb => tb.id === item.id)
                  : formSections.find(s => s.id === item.id);
                return section ? createFormField(section, pageIndex) : null;
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={`${item.id}-${pageIndex}-${itemIndex}`}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScale={(direction) => handlePhotoScale(item.textboxId, item.id, direction)}
                    onMove={(direction) => handlePhotoMove(item.textboxId, item.id, direction)}
                    onHeightChange={(height) => {
                      setPhotoHeights(prev => ({ ...prev, [item.id]: height }));
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === actualTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Schedule Performance Analysis - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Resource Utilization Report Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const ResourceUtilizationTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.resourceUtilization?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.resourceUtilization?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Table interaction state for hover and row management
  const [hoveredTable, setHoveredTable] = useState(null);
  const [focusedRowInfo, setFocusedRowInfo] = useState({ tableId: null, rowId: null });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('resources', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('resourceUtilization', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Get all items in order (tables + textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Add individual table items first (each table as separate item)
    const tableItems = [
      { type: 'table', id: 'labour', estimatedHeight: 25, tableId: 'labour' },
      { type: 'table', id: 'materialsOnSite', estimatedHeight: 25, tableId: 'materialsOnSite' },
      { type: 'table', id: 'plantOnSite', estimatedHeight: 25, tableId: 'plantOnSite' },
      { type: 'table', id: 'materialsNeeded', estimatedHeight: 25, tableId: 'materialsNeeded' },
      { type: 'table', id: 'plantNeeded', estimatedHeight: 25, tableId: 'plantNeeded' }
    ];
    
    tableItems.forEach(table => {
      allItems.push(table);
    });
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Add default textbox sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  // Row management functions
  const handleAddRow = (tableId, defaultStructure) => {
    const currentData = reportSections.resourceUtilization?.[tableId] || [];
    const newRow = { ...defaultStructure, id: `row_${Date.now()}` };
    const updatedData = [...currentData, newRow];
    updateReportSection('resourceUtilization', tableId, updatedData);
    console.log(`✅ Added row to ${tableId}`);
  };

  const handleDeleteRow = (tableId, rowId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Row',
      message: 'Are you sure you want to delete this row? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        const currentData = reportSections.resourceUtilization?.[tableId] || [];
        const updatedData = currentData.filter(row => row.id !== rowId);
        updateReportSection('resourceUtilization', tableId, updatedData);
        
        // Clear focus if deleted row was focused
        if (focusedRowInfo.rowId === rowId) {
          setFocusedRowInfo({ tableId: null, rowId: null });
        }
        
        console.log(`✅ Deleted row ${rowId} from ${tableId}`);
      }
    });
  };

  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('resourceUtilization', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('resourceUtilization', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Recalculate pages whenever section heights, user textboxes, photos, or table data changes  
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights, reportSections.resourceUtilization]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #0d9488', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>👥 9.0 Resource Utilization Report</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#f0fdfa', 
                color: '#0f766e', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#f0fdfa', 
            borderRadius: '4px', 
            border: '1px solid #5eead4' 
          }}>
            <p style={{ 
              color: '#0f766e', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Analysis of labour, equipment, and material utilization efficiency across the project
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'laborAnalysis',
      label: 'Labour Resource Analysis',
      placeholder: 'Analyze labour utilization patterns, workforce productivity, skill mix optimization, overtime usage, and staffing efficiency...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'equipmentAnalysis',
      label: 'Equipment Resource Analysis',
      placeholder: 'Document equipment utilization rates, maintenance schedules, downtime analysis, rental vs ownership costs, and equipment productivity metrics...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'materialManagement',
      label: 'Material Management & Supply Chain',
      placeholder: 'Report on material procurement efficiency, waste reduction initiatives, supply chain optimization, inventory management, and material cost control...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'optimizationStrategies',
      label: 'Resource Optimization Strategies',
      placeholder: 'Detail resource allocation improvements, cost reduction strategies, efficiency enhancements, and resource planning optimization...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'performanceBenchmarks',
      label: 'Performance Benchmarks & KPIs',
      placeholder: 'Compare resource utilization against industry benchmarks, project targets, historical performance, and best practices...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.resourceUtilization?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('resourceUtilization', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        />
      </div>
    );
  };

  // Helper function to get table data with auto-expansion
  const getTableData = (tableId, defaultStructure) => {
    const currentData = reportSections.resourceUtilization?.[tableId] || [];
    
    // Ensure we have at least 1 row
    const minRows = 1;
    const filledRows = currentData.length;
    
    // Check if all current rows have data (all fields filled)
    const allRowsFilled = currentData.length >= minRows && currentData.every(row => 
      Object.values(row).every(value => value && value.trim() !== '')
    );
    
    // If all rows are filled, add a new empty row
    if (allRowsFilled) {
      const newRow = { ...defaultStructure, id: `row_${Date.now()}` };
      const updatedData = [...currentData, newRow];
      updateReportSection('resourceUtilization', tableId, updatedData);
      return updatedData;
    }
    
    // If we don't have minimum rows, add empty rows
    if (filledRows < minRows) {
      const emptyRows = [];
      for (let i = filledRows; i < minRows; i++) {
        emptyRows.push({ ...defaultStructure, id: `row_${Date.now()}_${i}` });
      }
      const updatedData = [...currentData, ...emptyRows];
      updateReportSection('resourceUtilization', tableId, updatedData);
      return updatedData;
    }
    
    return currentData;
  };
  
  // Helper function to update table cell
  const updateTableCell = (tableId, rowId, field, value) => {
    const currentData = reportSections.resourceUtilization?.[tableId] || [];
    const updatedData = currentData.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    );
    updateReportSection('resourceUtilization', tableId, updatedData);
  };

  // Helper function to create hover action buttons for tables
  const createTableHoverButtons = (tableId, defaultStructure) => {
    if (hoveredTable !== tableId) return null;
    
    const canDeleteRow = focusedRowInfo.tableId === tableId && focusedRowInfo.rowId;
    
    return (
      <div style={{
        position: 'absolute',
        top: '2mm',
        right: '2mm',
        display: 'flex',
        gap: '2mm',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        padding: '2mm',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 10
      }}>
        <button
          type="button"
          onClick={() => handleAddRow(tableId, defaultStructure)}
          style={{
            border: '1px solid #10b981',
            borderRadius: '3px',
            backgroundColor: '#ecfdf5',
            color: '#10b981',
            padding: '1mm 2mm',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
          title="Add Row"
        >
          ➕
        </button>
        
        <button
          type="button"
          onClick={() => {
            if (canDeleteRow) {
              handleDeleteRow(focusedRowInfo.tableId, focusedRowInfo.rowId);
            }
          }}
          style={{
            border: '1px solid #dc2626',
            borderRadius: '3px',
            backgroundColor: canDeleteRow ? '#fef2f2' : '#f9fafb',
            color: canDeleteRow ? '#dc2626' : '#9ca3af',
            padding: '1mm 2mm',
            fontSize: '12px',
            cursor: canDeleteRow ? 'pointer' : 'not-allowed',
            fontWeight: 'bold'
          }}
          title={canDeleteRow ? "Delete Focused Row" : "Click on a row to delete"}
          disabled={!canDeleteRow}
        >
          ➖
        </button>
      </div>
    );
  };
  const calculateTotalWorkHours = () => {
    const labourData = reportSections.resourceUtilization?.labour || [];
    const total = labourData.reduce((sum, row) => {
      const hours = parseFloat(row.workHours) || 0;
      return sum + hours;
    }, 0);
    return total.toFixed(1);
  };

  // Default structures for different tables
  const materialsOnSiteDefault = { sn: '', item: '', description: '', quantity: '' };
  const materialsNeededDefault = { sn: '', item: '', description: '', quantity: '', dateNeeded: '' };
  const labourDefault = { sn: '', workerType: '', workHours: '' };
  const plantOnSiteDefault = { sn: '', item: '', description: '', quantity: '', hiredOwned: '' };
  const plantNeededDefault = { sn: '', item: '', description: '', quantity: '', dateNeeded: '' };

  // Individual table creation functions
  const createLabourTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('labour')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#374151', 
        marginBottom: '2mm' 
      }}>
        Labour
      </label>
      <div style={{ 
        backgroundColor: '#f9fafb',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #d1d5db',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('labour', labourDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Worker Type</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Work Hours ({calculateTotalWorkHours()})</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('labour', labourDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('labour', row.id, 'sn', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'labour', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <select 
                    value={row.workerType} 
                    onChange={(e) => updateTableCell('labour', row.id, 'workerType', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'labour', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  >
                    <option value="">Select Worker Type...</option>
                    <option value="Sub-Contractor">Sub-Contractor</option>
                    <option value="Site Manager">Site Manager</option>
                    <option value="Foreman">Foreman</option>
                    <option value="Skilled Worker">Skilled Worker</option>
                    <option value="Labourer">Labourer</option>
                    <option value="Other">Other</option>
                  </select>
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="number"
                    step="0.1"
                    value={row.workHours} 
                    onChange={(e) => updateTableCell('labour', row.id, 'workHours', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'labour', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createMaterialsOnSiteTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('materialsOnSite')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#374151', 
        marginBottom: '2mm' 
      }}>
        Materials on Site
      </label>
      <div style={{ 
        backgroundColor: '#f9fafb',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #d1d5db',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('materialsOnSite', materialsOnSiteDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('materialsOnSite', materialsOnSiteDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('materialsOnSite', row.id, 'sn', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.item} 
                    onChange={(e) => updateTableCell('materialsOnSite', row.id, 'item', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.description} 
                    onChange={(e) => updateTableCell('materialsOnSite', row.id, 'description', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.quantity} 
                    onChange={(e) => updateTableCell('materialsOnSite', row.id, 'quantity', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createPlantOnSiteTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('plantOnSite')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#374151', 
        marginBottom: '2mm' 
      }}>
        Plant / Equipment On Site
      </label>
      <div style={{ 
        backgroundColor: '#f9fafb',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #d1d5db',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('plantOnSite', plantOnSiteDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
              <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Hired / Owned</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('plantOnSite', plantOnSiteDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('plantOnSite', row.id, 'sn', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.item} 
                    onChange={(e) => updateTableCell('plantOnSite', row.id, 'item', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.description} 
                    onChange={(e) => updateTableCell('plantOnSite', row.id, 'description', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.quantity} 
                    onChange={(e) => updateTableCell('plantOnSite', row.id, 'quantity', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                  <select 
                    value={row.hiredOwned} 
                    onChange={(e) => updateTableCell('plantOnSite', row.id, 'hiredOwned', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  >
                    <option value="">Select...</option>
                    <option value="Hired">Hired</option>
                    <option value="Owned">Owned</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createMaterialsNeededTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('materialsNeeded')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#065f46', 
        marginBottom: '2mm' 
      }}>
        Materials Needed For Next Period
      </label>
      <div style={{ 
        backgroundColor: '#f0fdf4',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #bbf7d0',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('materialsNeeded', materialsNeededDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#dcfce7' }}>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Date Needed</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('materialsNeeded', materialsNeededDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('materialsNeeded', row.id, 'sn', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.item} 
                    onChange={(e) => updateTableCell('materialsNeeded', row.id, 'item', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.description} 
                    onChange={(e) => updateTableCell('materialsNeeded', row.id, 'description', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.quantity} 
                    onChange={(e) => updateTableCell('materialsNeeded', row.id, 'quantity', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="date" 
                    value={row.dateNeeded} 
                    onChange={(e) => updateTableCell('materialsNeeded', row.id, 'dateNeeded', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createPlantNeededTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('plantNeeded')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#065f46', 
        marginBottom: '2mm' 
      }}>
        Hired-In Plant / Equipment Needed For Next Period
      </label>
      <div style={{ 
        backgroundColor: '#f0fdf4',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #bbf7d0',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('plantNeeded', plantNeededDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#dcfce7' }}>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
              <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Date Needed</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('plantNeeded', plantNeededDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('plantNeeded', row.id, 'sn', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.item} 
                    onChange={(e) => updateTableCell('plantNeeded', row.id, 'item', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.description} 
                    onChange={(e) => updateTableCell('plantNeeded', row.id, 'description', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.quantity} 
                    onChange={(e) => updateTableCell('plantNeeded', row.id, 'quantity', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                  <input 
                    type="date" 
                    value={row.dateNeeded} 
                    onChange={(e) => updateTableCell('plantNeeded', row.id, 'dateNeeded', e.target.value)}
                    onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createResourceMetrics = () => {
    return (
      <div style={{ marginBottom: '6mm' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '14px', 
          fontWeight: '600', 
          color: '#111827', 
          marginBottom: '3mm' 
        }}>
          Resource Utilization Metrics
        </label>
        
        {/* 1. Labour - GREY THEME */}
        <div 
          style={{ marginBottom: '6mm', position: 'relative' }}
          onMouseEnter={() => setHoveredTable('labour')}
          onMouseLeave={() => setHoveredTable(null)}
        >
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '2mm' 
          }}>
            Labour
          </label>
          <div style={{ 
            backgroundColor: '#f9fafb',
            padding: '3mm',
            borderRadius: '2px',
            border: '1px solid #d1d5db',
            marginBottom: '4mm',
            position: 'relative'
          }}>
            {createTableHoverButtons('labour', labourDefault)}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Worker Type</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Work Hours ({calculateTotalWorkHours()})</th>
                </tr>
              </thead>
              <tbody>
                {getTableData('labour', labourDefault).map((row, index) => (
                  <tr key={row.id}>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.sn || (index + 1)} 
                        onChange={(e) => updateTableCell('labour', row.id, 'sn', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'labour', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <select 
                        value={row.workerType} 
                        onChange={(e) => updateTableCell('labour', row.id, 'workerType', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'labour', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      >
                        <option value="">Select Worker Type...</option>
                        <option value="Sub-Contractor">Sub-Contractor</option>
                        <option value="Site Manager">Site Manager</option>
                        <option value="Foreman">Foreman</option>
                        <option value="Skilled Worker">Skilled Worker</option>
                        <option value="Labourer">Labourer</option>
                        <option value="Other">Other</option>
                      </select>
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="number"
                        step="0.1"
                        value={row.workHours} 
                        onChange={(e) => updateTableCell('labour', row.id, 'workHours', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'labour', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 2. Materials on Site - GREY THEME */}
        <div 
          style={{ marginBottom: '6mm', position: 'relative' }}
          onMouseEnter={() => setHoveredTable('materialsOnSite')}
          onMouseLeave={() => setHoveredTable(null)}
        >
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '2mm' 
          }}>
            Materials on Site
          </label>
          <div style={{ 
            backgroundColor: '#f9fafb',
            padding: '3mm',
            borderRadius: '2px',
            border: '1px solid #d1d5db',
            marginBottom: '4mm',
            position: 'relative'
          }}>
            {createTableHoverButtons('materialsOnSite', materialsOnSiteDefault)}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
                </tr>
              </thead>
              <tbody>
                {getTableData('materialsOnSite', materialsOnSiteDefault).map((row, index) => (
                  <tr key={row.id}>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.sn || (index + 1)} 
                        onChange={(e) => updateTableCell('materialsOnSite', row.id, 'sn', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.item} 
                        onChange={(e) => updateTableCell('materialsOnSite', row.id, 'item', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.description} 
                        onChange={(e) => updateTableCell('materialsOnSite', row.id, 'description', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.quantity} 
                        onChange={(e) => updateTableCell('materialsOnSite', row.id, 'quantity', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 3. Plant / Equipment On Site - GREY THEME */}
        <div 
          style={{ marginBottom: '6mm', position: 'relative' }}
          onMouseEnter={() => setHoveredTable('plantOnSite')}
          onMouseLeave={() => setHoveredTable(null)}
        >
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '2mm' 
          }}>
            Plant / Equipment On Site
          </label>
          <div style={{ 
            backgroundColor: '#f9fafb',
            padding: '3mm',
            borderRadius: '2px',
            border: '1px solid #d1d5db',
            marginBottom: '4mm',
            position: 'relative'
          }}>
            {createTableHoverButtons('plantOnSite', plantOnSiteDefault)}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
                  <th style={{ border: '1px solid #d1d5db', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Hired / Owned</th>
                </tr>
              </thead>
              <tbody>
                {getTableData('plantOnSite', plantOnSiteDefault).map((row, index) => (
                  <tr key={row.id}>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.sn || (index + 1)} 
                        onChange={(e) => updateTableCell('plantOnSite', row.id, 'sn', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.item} 
                        onChange={(e) => updateTableCell('plantOnSite', row.id, 'item', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.description} 
                        onChange={(e) => updateTableCell('plantOnSite', row.id, 'description', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.quantity} 
                        onChange={(e) => updateTableCell('plantOnSite', row.id, 'quantity', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #d1d5db', padding: '1mm' }}>
                      <select 
                        value={row.hiredOwned} 
                        onChange={(e) => updateTableCell('plantOnSite', row.id, 'hiredOwned', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantOnSite', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      >
                        <option value="">Select...</option>
                        <option value="Hired">Hired</option>
                        <option value="Owned">Owned</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 4. Materials Needed For Next Period - LIGHT GREEN THEME */}
        <div 
          style={{ marginBottom: '6mm', position: 'relative' }}
          onMouseEnter={() => setHoveredTable('materialsNeeded')}
          onMouseLeave={() => setHoveredTable(null)}
        >
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#065f46', 
            marginBottom: '2mm' 
          }}>
            Materials Needed For Next Period
          </label>
          <div style={{ 
            backgroundColor: '#f0fdf4',
            padding: '3mm',
            borderRadius: '2px',
            border: '1px solid #bbf7d0',
            marginBottom: '4mm',
            position: 'relative'
          }}>
            {createTableHoverButtons('materialsNeeded', materialsNeededDefault)}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#dcfce7' }}>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Date Needed</th>
                </tr>
              </thead>
              <tbody>
                {getTableData('materialsNeeded', materialsNeededDefault).map((row, index) => (
                  <tr key={row.id}>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.sn || (index + 1)} 
                        onChange={(e) => updateTableCell('materialsNeeded', row.id, 'sn', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.item} 
                        onChange={(e) => updateTableCell('materialsNeeded', row.id, 'item', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.description} 
                        onChange={(e) => updateTableCell('materialsNeeded', row.id, 'description', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.quantity} 
                        onChange={(e) => updateTableCell('materialsNeeded', row.id, 'quantity', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="date" 
                        value={row.dateNeeded} 
                        onChange={(e) => updateTableCell('materialsNeeded', row.id, 'dateNeeded', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'materialsNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 5. Hired-In Plant / Equipment Needed For Next Period - LIGHT GREEN THEME */}
        <div 
          style={{ marginBottom: '6mm', position: 'relative' }}
          onMouseEnter={() => setHoveredTable('plantNeeded')}
          onMouseLeave={() => setHoveredTable(null)}
        >
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#065f46', 
            marginBottom: '2mm' 
          }}>
            Hired-In Plant / Equipment Needed For Next Period
          </label>
          <div style={{ 
            backgroundColor: '#f0fdf4',
            padding: '3mm',
            borderRadius: '2px',
            border: '1px solid #bbf7d0',
            marginBottom: '4mm',
            position: 'relative'
          }}>
            {createTableHoverButtons('plantNeeded', plantNeededDefault)}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#dcfce7' }}>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '50mm' }}>Item</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Description</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>Qty</th>
                  <th style={{ border: '1px solid #bbf7d0', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Date Needed</th>
                </tr>
              </thead>
              <tbody>
                {getTableData('plantNeeded', plantNeededDefault).map((row, index) => (
                  <tr key={row.id}>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.sn || (index + 1)} 
                        onChange={(e) => updateTableCell('plantNeeded', row.id, 'sn', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.item} 
                        onChange={(e) => updateTableCell('plantNeeded', row.id, 'item', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.description} 
                        onChange={(e) => updateTableCell('plantNeeded', row.id, 'description', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="text" 
                        value={row.quantity} 
                        onChange={(e) => updateTableCell('plantNeeded', row.id, 'quantity', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                    <td style={{ border: '1px solid #bbf7d0', padding: '1mm' }}>
                      <input 
                        type="date" 
                        value={row.dateNeeded} 
                        onChange={(e) => updateTableCell('plantNeeded', row.id, 'dateNeeded', e.target.value)}
                        onFocus={() => setFocusedRowInfo({ tableId: 'plantNeeded', rowId: row.id })}
                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like Section 2.0
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[item.id] || 50; // Default 50mm if not measured yet
      } else if (item.type === 'table') {
        // Calculate table height based on data and structure
        const tableData = reportSections.resourceUtilization?.[item.tableId] || [];
        const titleHeight = 8; // Table title height
        const headerHeight = 10; // Header row height (thicker)
        const rowHeight = 8; // Each data row height
        const containerPadding = 8; // Container padding and margins
        const numRows = Math.max(1, tableData.length); // At least 1 row
        itemHeight = titleHeight + headerHeight + (rowHeight * numRows) + containerPadding;
      } else if (item.type === 'table') {
        // More accurate table height calculation
        const tableData = reportSections.resourceUtilization?.[item.tableId] || [];
        const titleHeight = 8; // Table title height
        const headerHeight = 10; // Header row height (thicker)
        const rowHeight = 8; // Each data row height
        const containerPadding = 8; // Container padding and margins
        const numRows = Math.max(1, tableData.length); // At least 1 row
        itemHeight = titleHeight + headerHeight + (rowHeight * numRows) + containerPadding;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push([...currentPage]);
    }
    
    return pages.length > 0 ? pages : [[]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    // Fallback to calculatePages if pageLayout is empty (initial render)
    const pagesData = localTotalPages > 0 ? pageLayout : calculatePages();
    const actualTotalPages = pagesData.length;
    
    return pagesData.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('resources', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`resource-utilization-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, actualTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, itemIndex) => {
              if (item.type === 'table') {
                // Render individual tables
                switch (item.tableId) {
                  case 'labour':
                    return <div key={item.id}>{createLabourTable()}</div>;
                  case 'materialsOnSite':
                    return <div key={item.id}>{createMaterialsOnSiteTable()}</div>;
                  case 'plantOnSite':
                    return <div key={item.id}>{createPlantOnSiteTable()}</div>;
                  case 'materialsNeeded':
                    return <div key={item.id}>{createMaterialsNeededTable()}</div>;
                  case 'plantNeeded':
                    return <div key={item.id}>{createPlantNeededTable()}</div>;
                  default:
                    return null;
                }
              } else if (item.type === 'textbox') {
                const section = item.id.startsWith('userTextbox_') 
                  ? userTextboxes.find(tb => tb.id === item.id)
                  : formSections.find(s => s.id === item.id);
                return section ? createFormField(section, pageIndex) : null;
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={`${item.id}-${pageIndex}-${itemIndex}`}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScale={(direction) => handlePhotoScale(item.textboxId, item.id, direction)}
                    onMove={(direction) => handlePhotoMove(item.textboxId, item.id, direction)}
                    onHeightChange={(height) => {
                      setPhotoHeights(prev => ({ ...prev, [item.id]: height }));
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === actualTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Resource Utilization Report - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Budget Analysis & Financial Status Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const BudgetAnalysisTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.budgetAnalysis?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.budgetAnalysis?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('budget', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('budgetAnalysis', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Get all items in order (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('budgetAnalysis', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('budgetAnalysis', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Recalculate page layout when heights change
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  // Calculate revised budget based on cost breakdown analysis
  const calculateRevisedBudget = () => {
    const laborCosts = parseFloat(reportSections.budgetAnalysis?.laborCosts?.replace(/[£$,]/g, '') || '0');
    const materialCosts = parseFloat(reportSections.budgetAnalysis?.materialCosts?.replace(/[£$,]/g, '') || '0');
    const equipmentCosts = parseFloat(reportSections.budgetAnalysis?.equipmentCosts?.replace(/[£$,]/g, '') || '0');
    const overheadCosts = parseFloat(reportSections.budgetAnalysis?.overheadCosts?.replace(/[£$,]/g, '') || '0');
    
    const total = laborCosts + materialCosts + equipmentCosts + overheadCosts;
    return total.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  };

  // Dynamic calculation of actual metrics section height
  const calculateMetricsHeight = () => {
    // Convert CSS units to mm (approximate browser rendering)
    const px_to_mm = 0.264583; // 1px ≈ 0.264583mm at 96 DPI
    
    // Main container margins
    const containerMarginBottom = 6; // marginBottom: '6mm'
    
    // Main label: "Budget Performance Overview"
    const mainLabelFontSize = 14; // fontSize: '14px'
    const mainLabelHeight = (mainLabelFontSize * px_to_mm) + 3; // ~7mm (font + marginBottom: '3mm')
    
    // First section: Budget Performance Metrics
    const firstSectionMargin = 2; // marginBottom: '2mm'
    const firstContainerPadding = 8; // padding: '4mm' (top + bottom)
    const firstContainerBorder = 2; // border + borderRadius
    const firstContainerMargin = 3; // marginBottom: '3mm'
    const firstFieldHeight = 8; // Label (3mm) + input height (5mm)
    const firstSectionTotal = firstSectionMargin + firstContainerPadding + firstContainerBorder + firstFieldHeight + firstContainerMargin; // ~23mm
    
    // Second section: Cost Breakdown
    const secondContainerPadding = 8; // padding: '4mm' (top + bottom)
    const secondContainerBorder = 2; // border + borderRadius
    const secondFieldHeight = 20; // 4 fields with labels in 2x2 grid (~5mm each)
    const secondSectionTotal = secondContainerPadding + secondContainerBorder + secondFieldHeight; // ~30mm
    
    // Additional spacing and rendering overhead
    const renderingOverhead = 8; // Browser-specific spacing, line-height, etc.
    
    const totalHeight = containerMarginBottom + mainLabelHeight + firstSectionTotal + secondSectionTotal + renderingOverhead;
    
    return Math.ceil(totalHeight); // Round up for safety
  };

  // Handle budget field changes with calculation
  const handleBudgetFieldChange = (fieldId, value) => {
    updateReportSection('budgetAnalysis', fieldId, value);
    
    // Recalculate revised budget if it's a cost breakdown field
    if (['laborCosts', 'materialCosts', 'equipmentCosts', 'overheadCosts'].includes(fieldId)) {
      setTimeout(() => {
        const revisedTotal = calculateRevisedBudget();
        updateReportSection('budgetAnalysis', 'revisedBudget', revisedTotal);
      }, 100);
    }
  };

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #16a34a', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>💰 10.0 Budget Analysis & Financial Status</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#f0fdf4', 
                color: '#166534', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#f0fdf4', 
            borderRadius: '4px', 
            border: '1px solid #86efac' 
          }}>
            <p style={{ 
              color: '#166534', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Comprehensive financial analysis including budget performance, cost management, and variance reporting
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'varianceAnalysis',
      label: 'Cost Variance Analysis',
      placeholder: 'Analyze significant cost variances by category, identify root causes, and explain budget deviations. Include favorable and unfavorable variances...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'changeOrders',
      label: 'Change Orders & Contract Variations',
      placeholder: 'Document approved change orders, pending variations, cost impact analysis, and change management process effectiveness...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'cashFlow',
      label: 'Cash Flow & Payment Status',
      placeholder: 'Analyze project cash flow, payment schedules, outstanding invoices, and working capital requirements...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'costForecast',
      label: 'Cost Forecast & Future Projections',
      placeholder: 'Provide updated cost forecasts, estimate at completion (EAC), budget risks, and recommendations for cost control...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.budgetAnalysis?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('budgetAnalysis', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        />
      </div>
    );
  };

  const createBudgetMetrics = () => (
    <div style={{ marginBottom: '6mm' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '3mm' 
      }}>
        Budget Performance Overview
      </label>
      
      {/* Budget Performance Metrics */}
      <div style={{ marginBottom: '2mm' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '3mm',
          backgroundColor: '#f0fdf4',
          padding: '4mm',
          borderRadius: '2px',
          border: '1px solid #86efac',
          marginBottom: '3mm'
        }}>
          {[
            { id: 'originalBudget', label: 'Original Project Cost', placeholder: '£0', type: 'text', editable: true },
            { id: 'revisedBudget', label: 'Revised Project Cost', placeholder: '£0', type: 'text', editable: false }
          ].map(field => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#166534', 
                marginBottom: '1mm' 
              }}>
                {field.label}
              </label>
              <input
                type={field.type}
                step={field.step}
                value={field.id === 'revisedBudget' ? calculateRevisedBudget() : (reportSections.budgetAnalysis?.[field.id] || (field.editable ? '' : '£0'))}
                onChange={(e) => field.editable ? handleBudgetFieldChange(field.id, e.target.value) : null}
                readOnly={!field.editable}
                style={{
                  width: '100%',
                  padding: '2mm',
                  border: '1px solid #d1d5db',
                  borderRadius: '2px',
                  fontSize: '11px',
                  outline: 'none',
                  backgroundColor: field.editable ? 'white' : '#f9fafb',
                  cursor: field.editable ? 'text' : 'not-allowed'
                }}
                placeholder={field.placeholder}
                onFocus={(e) => {
                  if (field.editable) {
                    e.target.style.borderColor = '#16a34a';
                    e.target.style.boxShadow = '0 0 0 1px #16a34a';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Cost Breakdown */}
      <div>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '3mm',
          backgroundColor: '#f0fdf4',
          padding: '4mm',
          borderRadius: '2px',
          border: '1px solid #86efac'
        }}>
          {[
            { id: 'laborCosts', label: 'Labour Costs', placeholder: '£0' },
            { id: 'materialCosts', label: 'Material Costs', placeholder: '£0' },
            { id: 'equipmentCosts', label: 'Equipment Costs', placeholder: '£0' },
            { id: 'overheadCosts', label: 'Overhead & General Costs', placeholder: '£0' }
          ].map(field => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#166534', 
                marginBottom: '1mm' 
              }}>
                {field.label}
              </label>
              <input
                type="text"
                value={reportSections.budgetAnalysis?.[field.id] || ''}
                onChange={(e) => handleBudgetFieldChange(field.id, e.target.value)}
                style={{
                  width: '100%',
                  padding: '2mm',
                  border: '1px solid #d1d5db',
                  borderRadius: '2px',
                  fontSize: '11px',
                  outline: 'none'
                }}
                placeholder={field.placeholder}
                onFocus={(e) => {
                  e.target.style.borderColor = '#16a34a';
                  e.target.style.boxShadow = '0 0 0 1px #16a34a';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const METRICS_HEIGHT = calculateMetricsHeight(); // Dynamic calculation
    
    const allItems = getAllItems();
    const pages = [];
    let currentPage = [];
    let currentPageHeight = METRICS_HEIGHT; // Start with dynamic metrics height
    
    // Add metrics as first item (only on first page)
    currentPage.push({ type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT });
    
    allItems.forEach((item) => {
      const itemHeight = item.type === 'textbox' 
        ? (sectionHeights[item.id] || item.estimatedHeight || 25) + 12
        : (photoHeights[item.id] || 20) + 5;
      
      // Check if item fits on current page
      if (currentPageHeight + itemHeight > AVAILABLE_HEIGHT && currentPage.length > 0) {
        // Save current page and start new one (without metrics)
        pages.push([...currentPage]);
        currentPage = [];
        currentPageHeight = itemHeight;
        currentPage.push({ ...item, itemHeight });
      } else {
        // Add to current page
        currentPageHeight += itemHeight;
        currentPage.push({ ...item, itemHeight });
      }
    });
    
    // Add final page if it has content
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT }]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    return pageLayout.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('budget', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`budget-analysis-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, index) => {
              if (item.type === 'metrics') {
                return <div key="metrics">{createBudgetMetrics()}</div>;
              } else if (item.type === 'textbox') {
                return createFormField(item, pageIndex);
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={item.id}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScale={(direction) => handlePhotoScale(item.textboxId, item.id, direction)}
                    onMove={(direction) => handlePhotoMove(item.textboxId, item.id, direction)}
                    onHeightChange={(height) => {
                      setPhotoHeights(prev => ({ ...prev, [`${item.textboxId}_${item.id}`]: height }));
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Budget Analysis & Financial Status - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Risk Assessment & Mitigation Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const RiskAssessmentTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.riskAssessment?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.riskAssessment?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('risk', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('riskAssessment', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Get all items in order (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('riskAssessment', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('riskAssessment', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Recalculate pages whenever section heights, user textboxes, or photos change
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Dynamic calculation of actual risk metrics section height
  const calculateMetricsHeight = () => {
    // Convert CSS units to mm (approximate browser rendering)
    const px_to_mm = 0.264583; // 1px ≈ 0.264583mm at 96 DPI
    
    // Main container margins
    const containerMarginBottom = 6; // marginBottom: '6mm'
    
    // Main label: "Risk Management Overview"
    const mainLabelFontSize = 14; // fontSize: '14px'
    const mainLabelHeight = (mainLabelFontSize * px_to_mm) + 3; // ~7mm (font + marginBottom: '3mm')
    
    // First section: Risk Overview Metrics (4 fields in 1x4 grid)
    const firstSectionMargin = 4; // marginBottom: '4mm'
    const firstContainerPadding = 8; // padding: '4mm' (top + bottom)
    const firstContainerBorder = 2; // border + borderRadius
    const firstContainerMargin = 4; // marginBottom: '4mm'
    const firstFieldHeight = 8; // Label (2mm) + input height (6mm)
    const firstSectionTotal = firstSectionMargin + firstContainerPadding + firstContainerBorder + firstFieldHeight + firstContainerMargin; // ~26mm
    
    // Second section: Risk Category Analysis (5 number inputs in 1x5 grid)
    const secondContainerPadding = 8; // padding: '4mm' (top + bottom)
    const secondContainerBorder = 2; // border + borderRadius
    const secondFieldHeight = 8; // Label (2mm) + number input height (6mm)
    const secondSectionTotal = secondContainerPadding + secondContainerBorder + secondFieldHeight; // ~18mm
    
    // Additional spacing and rendering overhead
    const renderingOverhead = 8; // Browser-specific spacing, line-height, etc.
    
    const totalHeight = containerMarginBottom + mainLabelHeight + firstSectionTotal + secondSectionTotal + renderingOverhead;
    
    return Math.ceil(totalHeight); // Round up for safety (~65mm)
  };

  // Enhanced metrics calculation functions
  const handleMetricsChange = (fieldId, value) => {
    const totalRisks = fieldId === 'totalRisks' ? parseFloat(value) || 0 : parseFloat(reportSections.riskAssessment?.totalRisks) || 0;
    const risksMitigated = fieldId === 'risksMitigated' ? parseFloat(value) || 0 : parseFloat(reportSections.riskAssessment?.risksMitigated) || 0;
    const inputValue = parseFloat(value) || 0;
    
    // Validate that high priority risks and risks mitigated cannot exceed total risks
    if ((fieldId === 'highPriorityRisks' || fieldId === 'risksMitigated') && inputValue > totalRisks) {
      setErrorModal({
        isOpen: true,
        title: 'Invalid Value',
        message: `${fieldId === 'highPriorityRisks' ? 'High Priority Risks' : 'Risks Mitigated'} cannot exceed Total Risks Identified (${totalRisks}).`
      });
      return;
    }
    
    // Update the input field
    updateReportSection('riskAssessment', fieldId, value);
    
    // Calculate residual risk index after updating risks mitigated or total risks
    if (fieldId === 'risksMitigated' || fieldId === 'totalRisks') {
      const adjustedTotal = fieldId === 'totalRisks' ? inputValue : totalRisks;
      const adjustedMitigated = fieldId === 'risksMitigated' ? inputValue : risksMitigated;
      
      const residualIndex = adjustedTotal > 0 ? (adjustedMitigated / adjustedTotal).toFixed(2) : '0.00';
      updateReportSection('riskAssessment', 'residualRiskIndex', residualIndex);
    }
    
    // Calculate total risks from category breakdown if it's a category field
    if (['technicalRisks', 'financialRisks', 'environmentalRisks', 'operationalRisks', 'otherRisks'].includes(fieldId)) {
      const technical = parseFloat(reportSections.riskAssessment?.technicalRisks) || 0;
      const financial = parseFloat(reportSections.riskAssessment?.financialRisks) || 0;
      const environmental = parseFloat(reportSections.riskAssessment?.environmentalRisks) || 0;
      const operational = parseFloat(reportSections.riskAssessment?.operationalRisks) || 0;
      const other = parseFloat(reportSections.riskAssessment?.otherRisks) || 0;
      
      // Calculate new total based on which field changed
      const newTotal = 
        (fieldId === 'technicalRisks' ? inputValue : technical) +
        (fieldId === 'financialRisks' ? inputValue : financial) +
        (fieldId === 'environmentalRisks' ? inputValue : environmental) +
        (fieldId === 'operationalRisks' ? inputValue : operational) +
        (fieldId === 'otherRisks' ? inputValue : other);
      
      updateReportSection('riskAssessment', 'totalRisks', newTotal.toString());
      
      // Recalculate residual risk index with new total
      const currentMitigated = parseFloat(reportSections.riskAssessment?.risksMitigated) || 0;
      const newResidualIndex = newTotal > 0 ? (currentMitigated / newTotal).toFixed(2) : '0.00';
      updateReportSection('riskAssessment', 'residualRiskIndex', newResidualIndex);
    }
  };

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #dc2626', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>⚠️ 11.0 Risk Assessment & Mitigation</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#fef2f2', 
                color: '#991b1b', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#fef2f2', 
            borderRadius: '4px', 
            border: '1px solid #fca5a5' 
          }}>
            <p style={{ 
              color: '#991b1b', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Comprehensive risk analysis, impact assessment, and mitigation strategies for project risk management
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'technicalDesignRisks',
      label: 'Technical / Design Risks',
      placeholder: 'Detail technical risks including design changes, engineering challenges, specification issues, and technical implementation risks...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'financialCommercialRisks',
      label: 'Financial / Commercial Risks',
      placeholder: 'Analyze financial risks including cost escalation, payment delays, currency fluctuations, and commercial agreement issues...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'environmentalExternalRisks',
      label: 'Environmental / External Risks',
      placeholder: 'Document environmental risks including weather conditions, regulatory changes, site conditions, and external factors...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'operationalResourceRisks',
      label: 'Operational / Resource Risks',
      placeholder: 'Report operational risks including labor availability, equipment failure, supply chain issues, and resource constraints...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'otherRisks',
      label: 'Other Risks',
      placeholder: 'Document any other risks not covered in the above categories, including unique project-specific risks...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'highPriorityAnalysis',
      label: 'High Priority Risk Analysis',
      placeholder: 'Detail high-priority risks including weather delays, supply chain disruptions, regulatory changes, technical challenges, or resource availability issues...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'mitigationStrategies',
      label: 'Risk Mitigation Strategies',
      placeholder: 'Document mitigation strategies implemented, contingency plans activated, risk transfer mechanisms, and preventive measures taken...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'contingencyPlans',
      label: 'Contingency Plans & Response Actions',
      placeholder: 'Detail contingency plans for identified risks, response procedures, trigger points, and escalation processes...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'riskMonitoring',
      label: 'Risk Monitoring & Early Warning Systems',
      placeholder: 'Describe risk monitoring processes, key risk indicators (KRIs), early warning systems, and risk review procedures...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.riskAssessment?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('riskAssessment', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        />
      </div>
    );
  };

  const createRiskMetrics = () => (
    <div style={{ marginBottom: '6mm' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '3mm' 
      }}>
        Risk Management Overview
      </label>
      
      {/* Risk Overview Metrics - 4 fields redistributed equally */}
      <div style={{ marginBottom: '4mm' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr 1fr 1fr', 
          gap: '3mm',
          backgroundColor: '#fef2f2',
          padding: '4mm',
          borderRadius: '2px',
          border: '1px solid #fca5a5',
          marginBottom: '4mm'
        }}>
          {[
            { id: 'totalRisks', label: 'Total Risks Identified', placeholder: '0', editable: false },
            { id: 'highPriorityRisks', label: 'High Priority Risks', placeholder: '0', editable: true },
            { id: 'risksMitigated', label: 'Risks Mitigated', placeholder: '0', editable: true },
            { id: 'residualRiskIndex', label: 'Residual Risk Index', placeholder: '0.00', editable: false, tooltip: 'Ratio of mitigated risks to total risks. Index closer to 1.0 is good (most risks resolved). Lower index means several risks still need to be resolved.' }
          ].map(field => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: 'bold', 
                color: '#991b1b', 
                marginBottom: '1mm' 
              }}>
                {field.label}
                {field.tooltip && (
                  <span 
                    title={field.tooltip}
                    style={{ 
                      marginLeft: '2mm', 
                      cursor: 'help',
                      color: '#dc2626',
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}
                  >
                    ℹ️
                  </span>
                )}
              </label>
              <input
                type="number"
                min="0"
                value={field.id === 'residualRiskIndex' 
                  ? (reportSections.riskAssessment?.residualRiskIndex || '0.00')
                  : (reportSections.riskAssessment?.[field.id] || (field.editable ? '' : '0'))}
                onChange={(e) => field.editable ? handleMetricsChange(field.id, e.target.value) : null}
                readOnly={!field.editable}
                style={{
                  width: '100%',
                  padding: '2mm',
                  border: '1px solid #d1d5db',
                  borderRadius: '2px',
                  fontSize: '11px',
                  outline: 'none',
                  backgroundColor: field.editable ? 'white' : '#f9fafb',
                  cursor: field.editable ? 'text' : 'not-allowed'
                }}
                placeholder={field.placeholder}
                onFocus={(e) => {
                  if (field.editable) {
                    e.target.style.borderColor = '#dc2626';
                    e.target.style.boxShadow = '0 0 0 1px #dc2626';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Risk Category Analysis - Number inputs with tooltips */}
      <div>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', 
          gap: '3mm',
          backgroundColor: '#fef2f2',
          padding: '4mm',
          borderRadius: '2px',
          border: '1px solid #fca5a5'
        }}>
          {[
            { id: 'technicalRisks', label: 'Technical / Design', placeholder: '0', tooltip: 'Design changes, technical specifications, engineering challenges...' },
            { id: 'financialRisks', label: 'Financial / Commercial', placeholder: '0', tooltip: 'Cost escalation, payment delays, currency fluctuations...' },
            { id: 'environmentalRisks', label: 'Environmental / External', placeholder: '0', tooltip: 'Weather conditions, regulatory changes, site conditions...' },
            { id: 'operationalRisks', label: 'Operational / Resource', placeholder: '0', tooltip: 'Labor availability, equipment failure, supply chain issues...' },
            { id: 'otherRisks', label: 'Other', placeholder: '0', tooltip: 'Miscellaneous risks not covered in other categories...' }
          ].map(field => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#991b1b', 
                marginBottom: '1mm' 
              }}>
                {field.label}
              </label>
              <input
                type="number"
                min="0"
                value={reportSections.riskAssessment?.[field.id] || ''}
                onChange={(e) => handleMetricsChange(field.id, e.target.value)}
                style={{
                  width: '100%',
                  padding: '2mm',
                  border: '1px solid #d1d5db',
                  borderRadius: '2px',
                  fontSize: '11px',
                  outline: 'none'
                }}
                placeholder={field.placeholder}
                title={field.tooltip}
                onFocus={(e) => {
                  e.target.style.borderColor = '#dc2626';
                  e.target.style.boxShadow = '0 0 0 1px #dc2626';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    const METRICS_HEIGHT = calculateMetricsHeight(); // Dynamic calculation
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [{ type: 'metrics', itemHeight: METRICS_HEIGHT }];
    let currentPageUsedHeight = METRICS_HEIGHT;
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'metrics', itemHeight: METRICS_HEIGHT }]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    return pageLayout.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('risk', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`risk-assessment-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, index) => {
              if (item.type === 'metrics') {
                return <div key="metrics">{createRiskMetrics()}</div>;
              } else if (item.type === 'textbox') {
                return createFormField(item, pageIndex);
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={item.id}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScale={(direction) => handlePhotoScale(item.textboxId, item.id, direction)}
                    onMove={(direction) => handlePhotoMove(item.textboxId, item.id, direction)}
                    onHeightChange={(height) => {
                      setPhotoHeights(prev => ({ ...prev, [`${item.textboxId}_${item.id}`]: height }));
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Risk Assessment & Mitigation - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};
// Progress Comparisons (Before/After) Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const ProgressComparisonsTab = () => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  
  // State for managing photo grids and focus tracking
  const [photoGrids, setPhotoGrids] = useState(() => {
    const savedGrids = reportSections.progressComparisons?.photoGrids || [];
    // Always ensure at least one grid exists on load
    if (savedGrids.length === 0) {
      return [{
        id: `grid_${Date.now()}`,
        title: 'Photo Comparison Grid',
        leftPhoto: { id: `left_${Date.now()}`, url: null, name: '', label: '' },
        rightPhoto: { id: `right_${Date.now()}`, url: null, name: '', label: '' }
      }];
    }
    return savedGrids;
  });
  const [focusedGridId, setFocusedGridId] = useState(null);
  const [hoveredPhoto, setHoveredPhoto] = useState({ gridId: null, side: null });
  
  // Ref to track blur timeout for cancellation
  const blurTimeoutRef = useRef(null);
  
  // Modal state for work diary photos
  const [showWorkDiaryModal, setShowWorkDiaryModal] = useState(false);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState({ gridId: null, side: null });
  const [modalDateRange, setModalDateRange] = useState(() => {
    // Default to first day of current month and today
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    // Format dates properly without timezone issues
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      fromDate: formatDate(firstDayOfMonth),
      toDate: formatDate(today)
    };
  });
  const [loadedWorkDiaryPhotos, setLoadedWorkDiaryPhotos] = useState([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [selectedModalPhoto, setSelectedModalPhoto] = useState(null);
  
  // Custom modal states for replacing browser dialogs
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && photoGrids.length > 0) {
      const pages = Math.ceil(photoGrids.length / 2) || 1;
      updateSectionPageCount('comparisons', pages);
    }
  }, [photoGrids.length, updateSectionPageCount]);

  // Focus management helpers
  const handleFocusGrid = (gridId) => {
    // Cancel any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setFocusedGridId(gridId);
  };

  const handleBlurGrid = () => {
    // Cancel any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    
    // Set a new delayed timeout
    blurTimeoutRef.current = setTimeout(() => {
      setFocusedGridId(null);
      blurTimeoutRef.current = null;
    }, 200);
  };

  // Handle adding a new photo grid
  const handleAddPhotoGrid = () => {
    const newGrid = {
      id: `grid_${Date.now()}`,
      title: 'Photo Comparison Grid',
      leftPhoto: {
        id: `left_${Date.now()}`,
        url: null,
        name: '',
        label: ''
      },
      rightPhoto: {
        id: `right_${Date.now()}`,
        url: null,
        name: '',
        label: ''
      }
    };
    
    setPhotoGrids(prev => [...prev, newGrid]);
    console.log('✅ Added new photo grid');
  };

  // Handle loading work diary photos for date range
  const handleLoadWorkDiaryPhotos = async () => {
    if (!modalDateRange.fromDate || !modalDateRange.toDate) {
      setErrorModal({
        isOpen: true,
        title: 'Missing Dates',
        message: 'Please select both From Date and To Date'
      });
      return;
    }

    setIsLoadingPhotos(true);
    try {
      const response = await axios.get(`${API}/admin/work-diary-photos`, {
        params: {
          start_date: modalDateRange.fromDate,
          end_date: modalDateRange.toDate
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setLoadedWorkDiaryPhotos(response.data || []);
      console.log(`✅ Loaded ${response.data?.length || 0} work diary photos for range ${modalDateRange.fromDate} to ${modalDateRange.toDate}`);
    } catch (error) {
      console.error('Error loading work diary photos:', error);
      setErrorModal({
        isOpen: true,
        title: 'Load Failed',
        message: 'Error loading photos. Please try again.'
      });
      setLoadedWorkDiaryPhotos([]);
    } finally {
      setIsLoadingPhotos(false);
    }
  };
  const handleOpenWorkDiaryModal = (gridId, side) => {
    setSelectedPlaceholder({ gridId, side });
    setShowWorkDiaryModal(true);
  };

  // Handle work diary photo selection (just select, don't insert yet)
  const handleSelectModalPhoto = (photo) => {
    setSelectedModalPhoto(photo);
    console.log('✅ Selected photo for insertion:', photo.id);
  };

  // Handle inserting selected photo into grid
  const handleInsertSelectedPhoto = () => {
    if (selectedModalPhoto && selectedPlaceholder.gridId && selectedPlaceholder.side) {
      setPhotoGrids(prev => prev.map(grid => 
        grid.id === selectedPlaceholder.gridId 
          ? {
              ...grid,
              [`${selectedPlaceholder.side}Photo`]: {
                ...grid[`${selectedPlaceholder.side}Photo`],
                url: selectedModalPhoto.gcs_url || selectedModalPhoto.url,
                name: `Work Diary Photo - ${new Date(selectedModalPhoto.upload_date || selectedModalPhoto.created_at).toLocaleDateString()}`
              }
            }
          : grid
      ));
      console.log(`✅ Inserted work diary photo to ${selectedPlaceholder.side} side of grid ${selectedPlaceholder.gridId}`);
    }
    setShowWorkDiaryModal(false);
    setSelectedPlaceholder({ gridId: null, side: null });
    setLoadedWorkDiaryPhotos([]);
    setSelectedModalPhoto(null);
  };
  // Handle deleting a photo grid (prevent deleting last grid)
  const handleDeletePhotoGrid = (gridId) => {
    if (photoGrids.length <= 1) {
      setErrorModal({
        isOpen: true,
        title: 'Cannot Delete',
        message: 'Cannot delete the last photo grid. At least one grid must remain.'
      });
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Photo Grid',
      message: 'Are you sure you want to delete this photo grid? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setPhotoGrids(prev => prev.filter(grid => grid.id !== gridId));
        setFocusedGridId(null); // Clear focus when grid is deleted
        console.log(`✅ Deleted photo grid: ${gridId}`);
      }
    });
  };

  // Handle deleting a specific photo
  const handleDeletePhoto = (gridId, side) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Photo',
      message: 'Are you sure you want to delete this photo? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setPhotoGrids(prev => prev.map(grid => 
          grid.id === gridId 
            ? {
                ...grid,
                [`${side}Photo`]: {
                  ...grid[`${side}Photo`],
                  url: null,
                  name: ''
                }
              }
            : grid
        ));
        console.log(`✅ Deleted ${side} photo from grid ${gridId}`);
      }
    });
  };

  // Handle grid title update
  const handleGridTitleUpdate = (gridId, title) => {
    setPhotoGrids(prev => prev.map(grid => 
      grid.id === gridId 
        ? { ...grid, title: title }
        : grid
    ));
  };

  // Handle moving grids up/down with reliable state save
  const handleMoveGridUp = () => {
    if (!focusedGridId) return;
    
    // Force save any pending input values and wait longer for reliability
    const activeElement = document.activeElement;
    if (activeElement && activeElement.blur) {
      activeElement.blur();
    }
    
    // Use longer timeout for more reliable state saving
    setTimeout(() => {
      // Get current grids from state to ensure we have latest values
      setPhotoGrids(currentGrids => {
        const currentIndex = currentGrids.findIndex(grid => grid.id === focusedGridId);
        if (currentIndex > 0) {
          const newGrids = [...currentGrids];
          [newGrids[currentIndex - 1], newGrids[currentIndex]] = [newGrids[currentIndex], newGrids[currentIndex - 1]];
          console.log(`✅ Moved grid ${focusedGridId} up`);
          return newGrids;
        }
        return currentGrids;
      });
    }, 150); // Increased from 50ms to 150ms
  };

  const handleMoveGridDown = () => {
    if (!focusedGridId) return;
    
    // Force save any pending input values and wait longer for reliability
    const activeElement = document.activeElement;
    if (activeElement && activeElement.blur) {
      activeElement.blur();
    }
    
    // Use longer timeout for more reliable state saving
    setTimeout(() => {
      // Get current grids from state to ensure we have latest values
      setPhotoGrids(currentGrids => {
        const currentIndex = currentGrids.findIndex(grid => grid.id === focusedGridId);
        if (currentIndex < currentGrids.length - 1) {
          const newGrids = [...currentGrids];
          [newGrids[currentIndex + 1], newGrids[currentIndex]] = [newGrids[currentIndex], newGrids[currentIndex + 1]];
          console.log(`✅ Moved grid ${focusedGridId} down`);
          return newGrids;
        }
        return currentGrids;
      });
    }, 150); // Increased from 50ms to 150ms
  };

  // Handle photo upload for a specific placeholder
  const handlePhotoUpload = async (gridId, side) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrorModal({
          isOpen: true,
          title: 'File Too Large',
          message: 'File size must be less than 10MB'
        });
        return;
      }
      
      // Show uploading state
      setPhotoGrids(prev => prev.map(grid => 
        grid.id === gridId 
          ? {
              ...grid,
              [`${side}Photo`]: {
                ...grid[`${side}Photo`],
                uploading: true,
                name: file.name
              }
            }
          : grid
      ));
      
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API}/admin/reports/upload-photo`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        if (response.data.success) {
          setPhotoGrids(prev => prev.map(grid => 
            grid.id === gridId 
              ? {
                  ...grid,
                  [`${side}Photo`]: {
                    ...grid[`${side}Photo`],
                    url: response.data.gcs_url,
                    gcs_url: response.data.gcs_url,
                    name: file.name,
                    uploading: false
                  }
                }
              : grid
          ));
          console.log(`✅ Uploaded photo to ${side} side of grid ${gridId}`);
        }
      } catch (error) {
        console.error('Failed to upload photo:', error);
        const errorMessage = error.response?.data?.detail || 'Failed to upload photo. Please try again.';
        
        // Clear uploading state
        setPhotoGrids(prev => prev.map(grid => 
          grid.id === gridId 
            ? {
                ...grid,
                [`${side}Photo`]: {
                  ...grid[`${side}Photo`],
                  uploading: false
                }
              }
            : grid
        ));
        
        setErrorModal({
          isOpen: true,
          title: 'Upload Failed',
          message: errorMessage
        });
      }
    };
    
    input.click();
  };

  // Handle label update for photo placeholders
  const handleLabelUpdate = (gridId, side, label) => {
    setPhotoGrids(prev => prev.map(grid => 
      grid.id === gridId 
        ? {
            ...grid,
            [`${side}Photo`]: {
              ...grid[`${side}Photo`],
              label: label
            }
          }
        : grid
    ));
  };

  // Save photo grids to reportSections whenever they change
  useEffect(() => {
    updateReportSection('progressComparisons', 'photoGrids', photoGrids);
  }, [photoGrids]);

  // Register photo grid handlers and notify about focus changes
  useEffect(() => {
    console.log('Registering handlers, focusedGridId:', focusedGridId);
    
    window.progressComparisonsHandlers = {
      addGrid: handleAddPhotoGrid,
      deleteGrid: handleDeletePhotoGrid,
      moveGridUp: handleMoveGridUp,
      moveGridDown: handleMoveGridDown,
      getFocusedGridId: () => focusedGridId,
      hasFocusedGrid: () => !!focusedGridId,
      canMoveUp: () => {
        if (!focusedGridId) return false;
        const grids = reportSections.progressComparisons?.photoGrids || [];
        const currentIndex = grids.findIndex(grid => grid.id === focusedGridId);
        return currentIndex > 0;
      },
      canMoveDown: () => {
        if (!focusedGridId) return false;
        const grids = reportSections.progressComparisons?.photoGrids || [];
        const currentIndex = grids.findIndex(grid => grid.id === focusedGridId);
        return currentIndex < grids.length - 1;
      }
    };
    
    // Notify parent about focus state change
    const eventDetail = { hasFocusedGrid: !!focusedGridId };
    console.log('Dispatching progressComparisonsStateChange event:', eventDetail);
    window.dispatchEvent(new CustomEvent('progressComparisonsStateChange', { 
      detail: eventDetail 
    }));
    
    // Cleanup on unmount
    return () => {
      delete window.progressComparisonsHandlers;
    };
  }, [focusedGridId]); // Removed photoGrids dependency to prevent re-renders

  // Create photo grid component (reduced margins for 2 per page)
  const createPhotoGrid = (grid) => (
    <div key={grid.id} style={{ 
      marginBottom: '6mm', // Reduced from 10mm
      border: '1px solid #c4b5fd',
      borderRadius: '4px',
      padding: '4mm', // Reduced from 5mm
      backgroundColor: '#faf5ff'
    }}>
      {/* Grid header with editable title */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '3mm', // Reduced from 5mm
        paddingBottom: '2mm', // Reduced from 3mm
        borderBottom: '1px solid #e9d5ff'
      }}>
        <input
          type="text"
          value={grid.title || 'Photo Comparison Grid'}
          onChange={(e) => handleGridTitleUpdate(grid.id, e.target.value)}
          onFocus={() => setFocusedGridId(grid.id)}
          onBlur={() => setFocusedGridId(null)}
          style={{
            border: '1px solid transparent',
            outline: 'none',
            fontSize: '14px',
            fontWeight: '600',
            color: '#5b21b6',
            backgroundColor: 'transparent',
            cursor: 'text',
            minWidth: '200px',
            padding: '2px 4px',
            borderRadius: '2px'
          }}
          placeholder="Enter grid title..."
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.backgroundColor = '#f9fafb';
            handleFocusGrid(grid.id);
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'transparent';
            e.target.style.backgroundColor = 'transparent';
            handleBlurGrid();
          }}
        />
      </div>
      
      {/* Two photo placeholders side by side */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '3mm' // Reduced from 5mm
      }}>
        {/* Left photo placeholder */}
        <div style={{ textAlign: 'center' }}>
          <div 
            style={{
              width: '100%',
              height: '60mm',
              border: '2px dashed #c4b5fd',
              borderRadius: '4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: grid.leftPhoto.url ? 'transparent' : '#f9fafb',
              backgroundImage: grid.leftPhoto.url ? `url(${grid.leftPhoto.url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative'
            }}
            onMouseEnter={() => setHoveredPhoto({ gridId: grid.id, side: 'left' })}
            onMouseLeave={() => setHoveredPhoto({ gridId: null, side: null })}
          >
            {/* Uploading state */}
            {grid.leftPhoto.uploading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <svg style={{ width: '24px', height: '24px', marginBottom: '2mm' }} viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" fill="none" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="#7c3aed" strokeWidth="3" fill="none" strokeLinecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                  </path>
                </svg>
                <div style={{ fontSize: '9px', color: '#6b7280' }}>Uploading...</div>
              </div>
            )}
            
            {!grid.leftPhoto.url && !grid.leftPhoto.uploading && (
              <>
                <div style={{ fontSize: '16px', color: '#7c3aed', marginBottom: '2mm' }}>📷</div>
                <div style={{ fontSize: '10px', color: '#6b7280' }}>Before Photo</div>
              </>
            )}
            
            {/* Delete button (appears on hover when photo exists) */}
            {grid.leftPhoto.url && !grid.leftPhoto.uploading && hoveredPhoto.gridId === grid.id && hoveredPhoto.side === 'left' && (
              <button
                onClick={() => handleDeletePhoto(grid.id, 'left')}
                style={{
                  position: 'absolute',
                  top: '2mm',
                  right: '2mm',
                  border: '1px solid #dc2626',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  color: '#dc2626',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Delete Photo"
              >
                ✕
              </button>
            )}
            
            {/* Upload and Work Diary buttons (visible on hover when no photo and not uploading) */}
            {!grid.leftPhoto.url && !grid.leftPhoto.uploading && hoveredPhoto.gridId === grid.id && hoveredPhoto.side === 'left' && (
              <div style={{
                position: 'absolute',
                bottom: '2mm',
                left: '2mm',
                right: '2mm',
                display: 'flex',
                gap: '2mm',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => handleOpenWorkDiaryModal(grid.id, 'left')}
                  style={{
                    border: '1px solid #059669',
                    borderRadius: '3px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: '#059669',
                    padding: '1mm 2mm',
                    fontSize: '9px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    flex: 1
                  }}
                  title="Select from Work Diary Photos"
                >
                  📷 Work Diary Photos
                </button>
                
                <button
                  onClick={() => handlePhotoUpload(grid.id, 'left')}
                  style={{
                    border: '1px solid #7c3aed',
                    borderRadius: '3px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: '#7c3aed',
                    padding: '1mm 2mm',
                    fontSize: '9px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    flex: 1
                  }}
                  title="Upload Other Photo"
                >
                  📤 Upload Other
                </button>
              </div>
            )}
          </div>
          
          {/* Label textbox for left photo */}
          <textarea
            value={grid.leftPhoto.label}
            onChange={(e) => handleLabelUpdate(grid.id, 'left', e.target.value)}
            placeholder="Before: Description or label..."
            style={{
              width: '100%',
              marginTop: '2mm',
              padding: '2mm',
              border: '1px solid #d1d5db',
              borderRadius: '2px',
              fontSize: '10px',
              minHeight: '15mm',
              resize: 'none', // Fixed size
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#7c3aed';
              e.target.style.boxShadow = '0 0 0 1px #7c3aed';
              handleFocusGrid(grid.id);
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = 'none';
              handleBlurGrid();
            }}
          />
        </div>

        {/* Right photo placeholder */}
        <div style={{ textAlign: 'center' }}>
          <div 
            style={{
              width: '100%',
              height: '60mm',
              border: '2px dashed #c4b5fd',
              borderRadius: '4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: grid.rightPhoto.url ? 'transparent' : '#f9fafb',
              backgroundImage: grid.rightPhoto.url ? `url(${grid.rightPhoto.url})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative'
            }}
            onMouseEnter={() => setHoveredPhoto({ gridId: grid.id, side: 'right' })}
            onMouseLeave={() => setHoveredPhoto({ gridId: null, side: null })}
          >
            {/* Uploading state */}
            {grid.rightPhoto.uploading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <svg style={{ width: '24px', height: '24px', marginBottom: '2mm' }} viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" fill="none" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="#7c3aed" strokeWidth="3" fill="none" strokeLinecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                  </path>
                </svg>
                <div style={{ fontSize: '9px', color: '#6b7280' }}>Uploading...</div>
              </div>
            )}
            
            {!grid.rightPhoto.url && !grid.rightPhoto.uploading && (
              <>
                <div style={{ fontSize: '16px', color: '#7c3aed', marginBottom: '2mm' }}>📷</div>
                <div style={{ fontSize: '10px', color: '#6b7280' }}>After Photo</div>
              </>
            )}
            
            {/* Delete button (appears on hover when photo exists) */}
            {grid.rightPhoto.url && !grid.rightPhoto.uploading && hoveredPhoto.gridId === grid.id && hoveredPhoto.side === 'right' && (
              <button
                onClick={() => handleDeletePhoto(grid.id, 'right')}
                style={{
                  position: 'absolute',
                  top: '2mm',
                  right: '2mm',
                  border: '1px solid #dc2626',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  color: '#dc2626',
                  width: '20px',
                  height: '20px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Delete Photo"
              >
                ✕
              </button>
            )}
            
            {/* Upload and Work Diary buttons (visible on hover when no photo and not uploading) */}
            {!grid.rightPhoto.url && !grid.rightPhoto.uploading && hoveredPhoto.gridId === grid.id && hoveredPhoto.side === 'right' && (
              <div style={{
                position: 'absolute',
                bottom: '2mm',
                left: '2mm',
                right: '2mm',
                display: 'flex',
                gap: '2mm',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => handleOpenWorkDiaryModal(grid.id, 'right')}
                  style={{
                    border: '1px solid #059669',
                    borderRadius: '3px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: '#059669',
                    padding: '1mm 2mm',
                    fontSize: '9px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    flex: 1
                  }}
                  title="Select from Work Diary Photos"
                >
                  📷 Work Diary Photos
                </button>
                
                <button
                  onClick={() => handlePhotoUpload(grid.id, 'right')}
                  style={{
                    border: '1px solid #7c3aed',
                    borderRadius: '3px',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    color: '#7c3aed',
                    padding: '1mm 2mm',
                    fontSize: '9px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    flex: 1
                  }}
                  title="Upload Other Photo"
                >
                  📤 Upload Other
                </button>
              </div>
            )}
          </div>
          
          {/* Label textbox for right photo */}
          <textarea
            value={grid.rightPhoto.label}
            onChange={(e) => handleLabelUpdate(grid.id, 'right', e.target.value)}
            placeholder="After: Description or label..."
            style={{
              width: '100%',
              marginTop: '2mm',
              padding: '2mm',
              border: '1px solid #d1d5db',
              borderRadius: '2px',
              fontSize: '10px',
              minHeight: '15mm',
              resize: 'none', // Fixed size
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#7c3aed';
              e.target.style.boxShadow = '0 0 0 1px #7c3aed';
              handleFocusGrid(grid.id);
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.boxShadow = 'none';
              handleBlurGrid();
            }}
          />
        </div>
      </div>
    </div>
  );

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #7c3aed', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>↔️ 12.0 Progress Comparisons (Before/After)</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#f3f4f6', 
                color: '#5b21b6', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#f3f4f6', 
            borderRadius: '4px', 
            border: '1px solid #c4b5fd' 
          }}>
            <p style={{ 
              color: '#5b21b6', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Visual before/after comparisons showcasing construction progress through photo grids
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Calculate pages to fit 2 photo grids per page
  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    
    // Each photo grid height: 60mm photos + 15mm labels + 10mm header + 8mm margins = ~93mm
    const GRID_HEIGHT = 93; 
    const GRIDS_PER_PAGE = 2;
    const pages = [];
    
    for (let i = 0; i < photoGrids.length; i += GRIDS_PER_PAGE) {
      const pageGrids = photoGrids.slice(i, i + GRIDS_PER_PAGE);
      pages.push(pageGrids);
    }
    
    return pages.length > 0 ? pages : [photoGrids.slice(0, GRIDS_PER_PAGE)];
  };
  // Multipage content structure with 2 grids per page
  const createPages = () => {
    const pages = calculatePages();
    const localTotalPages = pages.length;
    
    return pages.map((pageGrids, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('comparisons', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`progress-comparisons-page-${pageIndex + 1}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden',
            padding: '10mm'
          }}>
            {pageGrids.length > 0 ? (
              pageGrids.map(grid => (
                <div key={grid.id} style={{ marginBottom: '8mm' }}>
                  {createPhotoGrid(grid)}
                </div>
              ))
            ) : (
              <div style={{ 
                textAlign: 'center',
                marginTop: '50mm',
                color: '#7c3aed',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                📷 No Photo Grids Yet
                <div style={{ 
                  marginTop: '10mm',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontWeight: 'normal'
                }}>
                  Click "Add Photo Grid" in the toolbar to create your first comparison grid
                </div>
              </div>
            )}
          </div>
          
          <div style={{ 
            borderTop: '1px solid #e5e7eb', 
            paddingTop: '3mm', 
            textAlign: 'center', 
            fontSize: '10px', 
            color: '#6b7280',
            height: '15mm',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            Progress Comparisons (Before/After) - SiteOrbit Construction Management System
          </div>
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Work Diary Photos Modal */}
      {showWorkDiaryModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={(e) => {
            // Backdrop click disabled - modal must be closed via buttons
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '750px', // 25% bigger than 600px
              width: '90%',
              maxHeight: '90vh', // Also increased height
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()} // Prevent modal close when clicking inside
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '10px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#111827' }}>
                Select Work Diary Photos
              </h3>
              <button
                onClick={() => setShowWorkDiaryModal(false)}
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
                title="Close"
              >
                ✕
              </button>
            </div>
            
            {/* Date Range Selectors */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr auto', 
              gap: '10px',
              marginBottom: '20px',
              alignItems: 'end'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>
                  From Date:
                </label>
                <input 
                  type="date"
                  value={modalDateRange.fromDate}
                  onChange={(e) => setModalDateRange(prev => ({ ...prev, fromDate: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '5px' }}>
                  To Date:
                </label>
                <input 
                  type="date"
                  value={modalDateRange.toDate}
                  onChange={(e) => setModalDateRange(prev => ({ ...prev, toDate: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                />
              </div>
              <button
                onClick={handleLoadWorkDiaryPhotos}
                disabled={isLoadingPhotos}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isLoadingPhotos ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: isLoadingPhotos ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                {isLoadingPhotos ? 'Loading...' : 'Load Photos'}
              </button>
            </div>
            
            {/* Photo selection area */}
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '10px'
            }}>
              {loadedWorkDiaryPhotos.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '10px'
                }}>
                  {loadedWorkDiaryPhotos.map(photo => (
                    <div 
                      key={photo.id}
                      onClick={() => handleSelectModalPhoto(photo)}
                      style={{
                        border: selectedModalPhoto?.id === photo.id ? '3px solid #3b82f6' : '2px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '5px',
                        cursor: 'pointer',
                        backgroundColor: selectedModalPhoto?.id === photo.id ? '#eff6ff' : '#f9fafb',
                        transition: 'all 0.2s',
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedModalPhoto?.id !== photo.id) {
                          e.target.style.borderColor = '#3b82f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedModalPhoto?.id !== photo.id) {
                          e.target.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      <img 
                        src={photo.gcs_url || photo.url}
                        alt={`Work diary photo ${photo.id}`}
                        style={{
                          width: '100%',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div style={{
                        display: 'none',
                        width: '100%',
                        height: '80px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        color: '#6b7280',
                        fontSize: '12px'
                      }}>
                        📷 Image Error
                      </div>
                      
                      {/* Selection indicator */}
                      {selectedModalPhoto?.id === photo.id && (
                        <div style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          width: '20px',
                          height: '20px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </div>
                      )}
                      
                      <div style={{
                        fontSize: '10px',
                        color: '#6b7280',
                        textAlign: 'center',
                        marginTop: '3px',
                        wordBreak: 'break-word'
                      }}>
                        {new Date(photo.upload_date || photo.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280'
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>📷</div>
                  <div>
                    {isLoadingPhotos 
                      ? 'Loading photos...' 
                      : 'Select date range and click "Load Photos" to view available work diary photos'
                    }
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div style={{
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{
                fontSize: '12px',
                color: '#6b7280'
              }}>
                {selectedModalPhoto ? `Selected: ${new Date(selectedModalPhoto.upload_date || selectedModalPhoto.created_at).toLocaleDateString()}` : 'No photo selected'}
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowWorkDiaryModal(false);
                    setSelectedModalPhoto(null);
                    setLoadedWorkDiaryPhotos([]);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleInsertSelectedPhoto}
                  disabled={!selectedModalPhoto}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: selectedModalPhoto ? '#10b981' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: selectedModalPhoto ? 'pointer' : 'not-allowed',
                    fontWeight: '500'
                  }}
                  title={selectedModalPhoto ? 'Insert selected photo into grid' : 'Select a photo first'}
                >
                  📷 Insert Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
};

// Technical Drawings & Plans Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const TechnicalDrawingsTab = () => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  // Table interaction state for hover and row management
  const [hoveredTable, setHoveredTable] = useState(null);
  const [focusedRowInfo, setFocusedRowInfo] = useState({ tableId: null, rowId: null });
  const [focusedTableInfo, setFocusedTableInfo] = useState({ tableId: null, isCustom: false });
  
  // Ref to track blur timeout for cancellation
  const blurTimeoutRef = useRef(null);
  
  // Custom tables state
  const [customTables, setCustomTables] = useState(() => {
    return reportSections.technicalDrawings?.customTables || [];
  });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('drawings', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Focus management helpers (like Section 12.0)
  const handleFocusTable = (tableId, isCustom = false) => {
    console.log(`Focus table: ${tableId}, isCustom: ${isCustom}`);
    // Cancel any pending blur timeout
    if (blurTimeoutRef.current) {
      console.log('Cancelling pending blur timeout');
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setFocusedTableInfo({ tableId, isCustom });
  };

  const handleBlurTable = () => {
    console.log('Blur table - setting timeout');
    // Cancel any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    
    // Set a new delayed timeout
    blurTimeoutRef.current = setTimeout(() => {
      console.log('Clearing table focus after timeout');
      setFocusedTableInfo({ tableId: null, isCustom: false });
      blurTimeoutRef.current = null;
    }, 200);
  };

  // Helper function to get table data with auto-expansion
  const getTableData = (tableId, defaultStructure) => {
    const currentData = reportSections.technicalDrawings?.[tableId] || [];
    
    // Ensure we have at least 1 row
    const minRows = 1;
    const filledRows = currentData.length;
    
    // Check if all current rows have data (all fields filled)
    const allRowsFilled = currentData.length >= minRows && currentData.every(row => 
      Object.values(row).every(value => value && value.trim() !== '')
    );
    
    // If all rows are filled, add a new empty row
    if (allRowsFilled) {
      const newRow = { ...defaultStructure, id: `row_${Date.now()}` };
      const updatedData = [...currentData, newRow];
      updateReportSection('technicalDrawings', tableId, updatedData);
      return updatedData;
    }
    
    // If we don't have minimum rows, add empty rows
    if (filledRows < minRows) {
      const emptyRows = [];
      for (let i = filledRows; i < minRows; i++) {
        emptyRows.push({ ...defaultStructure, id: `row_${Date.now()}_${i}` });
      }
      const updatedData = [...currentData, ...emptyRows];
      updateReportSection('technicalDrawings', tableId, updatedData);
      return updatedData;
    }
    
    return currentData;
  };
  
  // Helper function to update table cell
  const updateTableCell = (tableId, rowId, field, value) => {
    // Check if it's a custom table
    const isCustomTable = tableId.startsWith('custom_');
    
    if (isCustomTable) {
      const currentTables = customTables;
      const updatedTables = currentTables.map(table => 
        table.id === tableId 
          ? {
              ...table,
              rows: table.rows.map(row => 
                row.id === rowId ? { ...row, [field]: value } : row
              )
            }
          : table
      );
      setCustomTables(updatedTables);
    } else {
      const currentData = reportSections.technicalDrawings?.[tableId] || [];
      const updatedData = currentData.map(row => 
        row.id === rowId ? { ...row, [field]: value } : row
      );
      updateReportSection('technicalDrawings', tableId, updatedData);
    }
  };

  // Handle adding custom table
  const handleAddCustomTable = () => {
    if (!focusedTableInfo.tableId) {
      console.warn('⚠️ Cannot add table: no focused table');
      return;
    }
    
    const newTableId = `custom_${Date.now()}`;
    const newTable = {
      id: newTableId,
      parentTableId: focusedTableInfo.tableId, // Can be default table ID or custom table ID
      rows: [
        { id: `row_${Date.now()}_1`, sn: '1', drawingTitle: '', drawingNumber: '', revisionDate: '' },
        { id: `row_${Date.now()}_2`, sn: '2', drawingTitle: '', drawingNumber: '', revisionDate: '' }
      ]
    };
    
    setCustomTables(prev => [...prev, newTable]);
    console.log(`✅ Added custom table: ${newTableId} after ${focusedTableInfo.tableId}`);
  };

  // Handle deleting custom table
  const handleDeleteCustomTable = () => {
    if (!focusedTableInfo.tableId || !focusedTableInfo.isCustom) {
      console.warn('⚠️ Cannot delete: not a custom table or no focused table');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Custom Table',
      message: 'Are you sure you want to delete this custom table? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setCustomTables(prev => prev.filter(table => table.id !== focusedTableInfo.tableId));
        setFocusedTableInfo({ tableId: null, isCustom: false });
        console.log(`✅ Deleted custom table: ${focusedTableInfo.tableId}`);
      }
    });
  };

  // Handle adding row to custom tables
  const handleAddRowToCustomTable = (tableId) => {
    const table = customTables.find(t => t.id === tableId);
    if (!table) return;
    
    const newRow = { 
      id: `row_${Date.now()}`, 
      sn: (table.rows.length + 1).toString(), 
      drawingTitle: '', 
      drawingNumber: '', 
      revisionDate: '' 
    };
    
    setCustomTables(prev => prev.map(t => 
      t.id === tableId 
        ? { ...t, rows: [...t.rows, newRow] }
        : t
    ));
    console.log(`✅ Added row to custom table ${tableId}`);
  };

  // Handle deleting row from custom tables
  const handleDeleteRowFromCustomTable = (tableId, rowId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Row',
      message: 'Are you sure you want to delete this row? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setCustomTables(prev => prev.map(t => 
          t.id === tableId 
            ? { ...t, rows: t.rows.filter(row => row.id !== rowId) }
            : t
        ));
        
        // Clear focus if deleted row was focused
        if (focusedRowInfo.rowId === rowId) {
          setFocusedRowInfo({ tableId: null, rowId: null });
        }
        
        console.log(`✅ Deleted row ${rowId} from custom table ${tableId}`);
      }
    });
  };

  // Recalculate pages whenever table data changes
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [reportSections.technicalDrawings, customTables]);

  // Save custom tables to reportSections whenever they change
  useEffect(() => {
    updateReportSection('technicalDrawings', 'customTables', customTables);
  }, [customTables]);

  // Register table management handlers with parent and notify state changes
  useEffect(() => {
    window.technicalDrawingsHandlers = {
      addTable: handleAddCustomTable,
      deleteTable: handleDeleteCustomTable,
      getFocusedTableInfo: () => focusedTableInfo
    };
    
    // Notify parent about focus state change
    window.dispatchEvent(new CustomEvent('technicalDrawingsStateChange', { 
      detail: { 
        hasTableFocus: !!focusedTableInfo.tableId,
        isCustomTable: focusedTableInfo.isCustom
      } 
    }));
    
    // Cleanup on unmount
    return () => {
      delete window.technicalDrawingsHandlers;
    };
  }, [focusedTableInfo, customTables]);

  // Row management functions (updated for custom tables)
  const handleAddRow = (tableId, defaultStructure) => {
    if (tableId.startsWith('custom_')) {
      handleAddRowToCustomTable(tableId);
    } else {
      const currentData = reportSections.technicalDrawings?.[tableId] || [];
      const newRow = { ...defaultStructure, id: `row_${Date.now()}` };
      const updatedData = [...currentData, newRow];
      updateReportSection('technicalDrawings', tableId, updatedData);
      console.log(`✅ Added row to ${tableId}`);
    }
  };

  const handleDeleteRow = (tableId, rowId) => {
    if (tableId.startsWith('custom_')) {
      handleDeleteRowFromCustomTable(tableId, rowId);
    } else {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Row',
        message: 'Are you sure you want to delete this row? This action cannot be undone.',
        onConfirm: () => {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
          const currentData = reportSections.technicalDrawings?.[tableId] || [];
          const updatedData = currentData.filter(row => row.id !== rowId);
          updateReportSection('technicalDrawings', tableId, updatedData);
          
          // Clear focus if deleted row was focused
          if (focusedRowInfo.rowId === rowId) {
            setFocusedRowInfo({ tableId: null, rowId: null });
          }
          
          console.log(`✅ Deleted row ${rowId} from ${tableId}`);
        }
      });
    }
  };

  // Helper function to create hover action buttons for tables
  const createTableHoverButtons = (tableId, defaultStructure) => {
    if (hoveredTable !== tableId) return null;
    
    const canDeleteRow = focusedRowInfo.tableId === tableId && focusedRowInfo.rowId;
    
    return (
      <div style={{
        position: 'absolute',
        top: '2mm',
        right: '2mm',
        display: 'flex',
        gap: '2mm',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        padding: '2mm',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 10
      }}>
        <button
          type="button"
          onClick={() => handleAddRow(tableId, defaultStructure)}
          style={{
            border: '1px solid #10b981',
            borderRadius: '3px',
            backgroundColor: '#ecfdf5',
            color: '#10b981',
            padding: '1mm 2mm',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
          title="Add Row"
        >
          ➕
        </button>
        
        <button
          type="button"
          onClick={() => {
            if (canDeleteRow) {
              handleDeleteRow(focusedRowInfo.tableId, focusedRowInfo.rowId);
            }
          }}
          style={{
            border: '1px solid #dc2626',
            borderRadius: '3px',
            backgroundColor: canDeleteRow ? '#fef2f2' : '#f9fafb',
            color: canDeleteRow ? '#dc2626' : '#9ca3af',
            padding: '1mm 2mm',
            fontSize: '12px',
            cursor: canDeleteRow ? 'pointer' : 'not-allowed',
            fontWeight: 'bold'
          }}
          title={canDeleteRow ? "Delete Focused Row" : "Click on a row to delete"}
          disabled={!canDeleteRow}
        >
          ➖
        </button>
      </div>
    );
  };

  // Custom table component (no title, no header row)
  const createCustomTable = (customTable) => (
    <div 
      key={customTable.id}
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable(customTable.id)}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <div style={{ 
        backgroundColor: '#f1f5f9',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #94a3b8',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons(customTable.id, { sn: '', drawingTitle: '', drawingNumber: '', revisionDate: '' })}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <tbody>
            {customTable.rows.map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #94a3b8', padding: '1mm', width: '15mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell(customTable.id, row.id, 'sn', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: customTable.id, rowId: row.id });
                      handleFocusTable(customTable.id, true);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #94a3b8', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingTitle} 
                    onChange={(e) => updateTableCell(customTable.id, row.id, 'drawingTitle', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: customTable.id, rowId: row.id });
                      handleFocusTable(customTable.id, true);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #94a3b8', padding: '1mm', width: '30mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingNumber} 
                    onChange={(e) => updateTableCell(customTable.id, row.id, 'drawingNumber', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: customTable.id, rowId: row.id });
                      handleFocusTable(customTable.id, true);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #94a3b8', padding: '1mm', width: '30mm' }}>
                  <input 
                    type="date" 
                    value={row.revisionDate} 
                    onChange={(e) => updateTableCell(customTable.id, row.id, 'revisionDate', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: customTable.id, rowId: row.id });
                      handleFocusTable(customTable.id, true);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #2563eb', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>📐 13.0 Technical Drawings & Plans</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#eff6ff', 
                color: '#1d4ed8', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#eff6ff', 
            borderRadius: '4px', 
            border: '1px solid #93c5fd' 
          }}>
            <p style={{ 
              color: '#1d4ed8', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Engineering documentation, technical drawings, plans, and specifications supporting the construction process
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Default structures for different tables
  const architecturalDefault = { sn: '', drawingTitle: '', drawingNumber: '', revisionDate: '' };
  const structuralDefault = { sn: '', drawingTitle: '', drawingNumber: '', revisionDate: '' };
  const mepDefault = { sn: '', drawingTitle: '', drawingNumber: '', revisionDate: '' };
  const otherDefault = { sn: '', drawingTitle: '', drawingNumber: '', revisionDate: '' };

  // Individual table creation functions
  const createArchitecturalTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('architectural')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#1d4ed8', 
        marginBottom: '2mm' 
      }}>
        Architectural Documentation
      </label>
      <div style={{ 
        backgroundColor: '#eff6ff',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #93c5fd',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('architectural', architecturalDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#dbeafe' }}>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Drawing Title</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Drawing Number</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Revision Date</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('architectural', architecturalDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('architectural', row.id, 'sn', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'architectural', rowId: row.id });
                      handleFocusTable('architectural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingTitle} 
                    onChange={(e) => updateTableCell('architectural', row.id, 'drawingTitle', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'architectural', rowId: row.id });
                      handleFocusTable('architectural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingNumber} 
                    onChange={(e) => updateTableCell('architectural', row.id, 'drawingNumber', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'architectural', rowId: row.id });
                      handleFocusTable('architectural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="date" 
                    value={row.revisionDate} 
                    onChange={(e) => updateTableCell('architectural', row.id, 'revisionDate', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'architectural', rowId: row.id });
                      handleFocusTable('architectural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createStructuralTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('structural')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#1d4ed8', 
        marginBottom: '2mm' 
      }}>
        Structural Documentation
      </label>
      <div style={{ 
        backgroundColor: '#eff6ff',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #93c5fd',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('structural', structuralDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#dbeafe' }}>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Drawing Title</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Drawing Number</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Revision Date</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('structural', structuralDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('structural', row.id, 'sn', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'structural', rowId: row.id });
                      handleFocusTable('structural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingTitle} 
                    onChange={(e) => updateTableCell('structural', row.id, 'drawingTitle', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'structural', rowId: row.id });
                      handleFocusTable('structural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingNumber} 
                    onChange={(e) => updateTableCell('structural', row.id, 'drawingNumber', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'structural', rowId: row.id });
                      handleFocusTable('structural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="date" 
                    value={row.revisionDate} 
                    onChange={(e) => updateTableCell('structural', row.id, 'revisionDate', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'structural', rowId: row.id });
                      handleFocusTable('structural', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createMEPTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('mep')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#1d4ed8', 
        marginBottom: '2mm' 
      }}>
        MEP Documentation
      </label>
      <div style={{ 
        backgroundColor: '#eff6ff',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #93c5fd',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('mep', mepDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#dbeafe' }}>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Drawing Title</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Drawing Number</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Revision Date</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('mep', mepDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('mep', row.id, 'sn', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'mep', rowId: row.id });
                      handleFocusTable('mep', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingTitle} 
                    onChange={(e) => updateTableCell('mep', row.id, 'drawingTitle', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'mep', rowId: row.id });
                      handleFocusTable('mep', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingNumber} 
                    onChange={(e) => updateTableCell('mep', row.id, 'drawingNumber', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'mep', rowId: row.id });
                      handleFocusTable('mep', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="date" 
                    value={row.revisionDate} 
                    onChange={(e) => updateTableCell('mep', row.id, 'revisionDate', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'mep', rowId: row.id });
                      handleFocusTable('mep', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const createOtherTable = () => (
    <div 
      style={{ marginBottom: '6mm', position: 'relative' }}
      onMouseEnter={() => setHoveredTable('other')}
      onMouseLeave={() => setHoveredTable(null)}
    >
      <label style={{ 
        display: 'block', 
        fontSize: '13px', 
        fontWeight: '600', 
        color: '#1d4ed8', 
        marginBottom: '2mm' 
      }}>
        Other Documentation
      </label>
      <div style={{ 
        backgroundColor: '#eff6ff',
        padding: '3mm',
        borderRadius: '2px',
        border: '1px solid #93c5fd',
        marginBottom: '4mm',
        position: 'relative'
      }}>
        {createTableHoverButtons('other', otherDefault)}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ backgroundColor: '#dbeafe' }}>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '15mm' }}>S/N</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600' }}>Drawing Title</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Drawing Number</th>
              <th style={{ border: '1px solid #93c5fd', padding: '2mm', textAlign: 'left', fontWeight: '600', width: '30mm' }}>Revision Date</th>
            </tr>
          </thead>
          <tbody>
            {getTableData('other', otherDefault).map((row, index) => (
              <tr key={row.id}>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.sn || (index + 1)} 
                    onChange={(e) => updateTableCell('other', row.id, 'sn', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'other', rowId: row.id });
                      handleFocusTable('other', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px', textAlign: 'center' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingTitle} 
                    onChange={(e) => updateTableCell('other', row.id, 'drawingTitle', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'other', rowId: row.id });
                      handleFocusTable('other', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="text" 
                    value={row.drawingNumber} 
                    onChange={(e) => updateTableCell('other', row.id, 'drawingNumber', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'other', rowId: row.id });
                      handleFocusTable('other', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
                <td style={{ border: '1px solid #93c5fd', padding: '1mm' }}>
                  <input 
                    type="date" 
                    value={row.revisionDate} 
                    onChange={(e) => updateTableCell('other', row.id, 'revisionDate', e.target.value)}
                    onFocus={() => {
                      setFocusedRowInfo({ tableId: 'other', rowId: row.id });
                      handleFocusTable('other', false);
                    }}
                    onBlur={handleBlurTable}
                    style={{ width: '100%', border: 'none', outline: 'none', fontSize: '13px' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Duplicate createOtherTable function removed

  const createDrawingsMetrics = () => (
    <div style={{ marginBottom: '6mm' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '3mm' 
      }}>
        Drawing Management Overview
      </label>
      
      {/* Drawing Management Metrics - Removed Pending Approvals */}
      <div style={{ marginBottom: '4mm' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr 1fr', 
          gap: '3mm',
          backgroundColor: '#eff6ff',
          padding: '4mm',
          borderRadius: '2px',
          border: '1px solid #93c5fd',
          marginBottom: '4mm'
        }}>
          {[
            { id: 'totalDrawings', label: 'Total Drawings', placeholder: '0' },
            { id: 'revisions', label: 'Revisions This Period', placeholder: '0' },
            { id: 'completedDrawings', label: 'New Drawings This Period', placeholder: '0' }
          ].map(field => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#1d4ed8', 
                marginBottom: '1mm' 
              }}>
                {field.label}
              </label>
              <input
                type="number"
                min="0"
                value={reportSections.technicalDrawings?.[field.id] || ''}
                onChange={(e) => updateReportSection('technicalDrawings', field.id, e.target.value)}
                style={{
                  width: '100%',
                  padding: '2mm',
                  border: '1px solid #d1d5db',
                  borderRadius: '2px',
                  fontSize: '11px',
                  outline: 'none'
                }}
                placeholder={field.placeholder}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.boxShadow = '0 0 0 1px #2563eb';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Get all items in order (default tables + custom tables in correct positions)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add custom tables after a parent table
    const addCustomTablesAfterParent = (parentTableId) => {
      const childTables = customTables.filter(ct => ct.parentTableId === parentTableId);
      console.log(`Adding ${childTables.length} custom tables after parent ${parentTableId}`);
      childTables.forEach(customTable => {
        allItems.push({ type: 'custom-table', id: customTable.id, tableId: customTable.id, customTable });
      });
    };
    
    // Add default table items with their custom tables in correct order
    const defaultTableItems = [
      { type: 'table', id: 'architectural', tableId: 'architectural' },
      { type: 'table', id: 'structural', tableId: 'structural' },
      { type: 'table', id: 'mep', tableId: 'mep' },
      { type: 'table', id: 'other', tableId: 'other' }
    ];
    
    defaultTableItems.forEach(table => {
      allItems.push(table);
      // Add custom tables that belong after this specific default table
      addCustomTablesAfterParent(table.tableId);
      console.log(`Processed table ${table.tableId}, current allItems length: ${allItems.length}`);
    });
    
    return allItems;
  };

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0;
    
    // Add metrics as first item manually
    const METRICS_HEIGHT = 25;
    const metricsItem = { type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT };
    if (currentPageUsedHeight + METRICS_HEIGHT <= AVAILABLE_HEIGHT) {
      currentPage.push(metricsItem);
      currentPageUsedHeight += METRICS_HEIGHT;
    }
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'table' || item.type === 'custom-table') {
        // Calculate table height based on data and structure (increased by 2-3mm each)
        let tableData;
        if (item.type === 'custom-table') {
          tableData = item.customTable.rows || [];
        } else {
          tableData = reportSections.technicalDrawings?.[item.tableId] || [];
        }
        
        const titleHeight = item.type === 'custom-table' ? 0 : 10; // No title for custom tables
        const headerHeight = item.type === 'custom-table' ? 0 : 12; // No header row for custom tables
        const rowHeight = 10; // Each data row height
        const containerPadding = 10; // Container padding and margins
        const numRows = Math.max(1, tableData.length); // At least 1 row
        itemHeight = titleHeight + headerHeight + (rowHeight * numRows) + containerPadding;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT }]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    return pageLayout.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('drawings', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`technical-drawings-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, itemIndex) => {
              if (item.type === 'metrics') {
                return <div key="metrics">{createDrawingsMetrics()}</div>;
              } else if (item.type === 'table') {
                // Render individual default tables
                switch (item.tableId) {
                  case 'architectural':
                    return <div key={item.id}>{createArchitecturalTable()}</div>;
                  case 'structural':
                    return <div key={item.id}>{createStructuralTable()}</div>;
                  case 'mep':
                    return <div key={item.id}>{createMEPTable()}</div>;
                  case 'other':
                    return <div key={item.id}>{createOtherTable()}</div>;
                  default:
                    return null;
                }
              } else if (item.type === 'custom-table') {
                // Render custom tables
                return createCustomTable(item.customTable);
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Technical Drawings & Plans - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
    </div>
  );
};

// Next Phase Planning Tab with A4 multi-page formatting - AUTOMATIC PAGE BREAKS
export const NextPhaseTab = ({ toolbarCommandRef, textboxActionsRef, onRichTextFocusChange, onTextboxInfoChange }) => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();
  const [sectionHeights, setSectionHeights] = useState({});
  const [photoHeights, setPhotoHeights] = useState({});
  const [pageLayout, setPageLayout] = useState([[]]);
  
  const [userTextboxes, setUserTextboxes] = useState(() => {
    return reportSections.nextPhase?.userTextboxes || [];
  });
  const [focusedTextboxId, setFocusedTextboxId] = useState(null);
  
  const [textboxPhotos, setTextboxPhotos] = useState(() => {
    return reportSections.nextPhase?.textboxPhotos || {};
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Report page count to parent context
  useEffect(() => {
    if (updateSectionPageCount && pageLayout.length > 0) {
      updateSectionPageCount('nextphase', pageLayout.length);
    }
  }, [pageLayout.length, updateSectionPageCount]);

  // Handle height updates from editors  
  const handleHeightUpdate = (data) => {
    const { editorId, contentHeight } = data;
    
    setSectionHeights(prev => ({ 
      ...prev, 
      [editorId]: contentHeight 
    }));
  };
  
  // Handle adding textbox
  const handleAddTextbox = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add textbox: no focused textbox');
      return;
    }
    
    const newTextboxId = `userTextbox_${Date.now()}`;
    const newTextbox = {
      id: newTextboxId,
      label: `Custom Section`,
      placeholder: 'Enter your custom content here...',
      parentId: focusedTextboxId,
      estimatedHeight: 30
    };
    
    setUserTextboxes(prev => [...prev, newTextbox]);
    console.log(`✅ Added textbox: ${newTextboxId} after ${focusedTextboxId}`);
  };
  
  // Handle deleting textbox
  const handleDeleteTextbox = () => {
    if (!focusedTextboxId || !focusedTextboxId.startsWith('userTextbox_')) {
      console.warn('⚠️ Cannot delete: not a user textbox or no focused textbox');
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Textbox',
      message: 'Are you sure you want to delete this textbox? This action cannot be undone.',
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        // Remove textbox
        setUserTextboxes(prev => prev.filter(tb => tb.id !== focusedTextboxId));
        
        // Remove associated photos
        setTextboxPhotos(prev => {
          const newPhotos = { ...prev };
          delete newPhotos[focusedTextboxId];
          return newPhotos;
        });
        
        // Remove content from reportSections
        updateReportSection('nextPhase', focusedTextboxId, '');
        
        // Clear focus
        setFocusedTextboxId(null);
        
        console.log(`✅ Deleted textbox: ${focusedTextboxId}`);
      }
    });
  };
  
  // Get all items in order (textboxes + photos)
  const getAllItems = () => {
    const allItems = [];
    
    // Helper to add textbox and all its children recursively
    const addTextboxAndChildren = (parentId) => {
      const childTextboxes = userTextboxes.filter(tb => tb.parentId === parentId);
      
      childTextboxes.forEach(tb => {
        // Add the user textbox
        allItems.push({ type: 'textbox', ...tb });
        
        // Add photos for this textbox
        const photos = textboxPhotos[tb.id] || [];
        photos.forEach(photo => {
          allItems.push({ type: 'photo', ...photo, textboxId: tb.id });
        });
        
        // Recursively add any textboxes that come after this user textbox
        addTextboxAndChildren(tb.id);
      });
    };
    
    // Start with default sections and build the tree
    formSections.forEach(section => {
      // Add textbox
      allItems.push({ type: 'textbox', ...section });
      
      // Add photos for this textbox
      const photos = textboxPhotos[section.id] || [];
      photos.forEach(photo => {
        allItems.push({ type: 'photo', ...photo, textboxId: section.id });
      });
      
      // Add child textboxes and their photos
      addTextboxAndChildren(section.id);
    });
    
    return allItems;
  };
  
  // Handle adding photos after the currently focused textbox
  const handleAddPhotos = () => {
    if (!focusedTextboxId) {
      console.warn('⚠️ Cannot add photos: no focused textbox');
      return;
    }
    
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      // Convert files to data URLs
      Promise.all(
        files.map(file => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                url: e.target.result,
                name: file.name,
                scale: 50, // Default to 50% (middle of 30-75% range)
                textboxId: focusedTextboxId
              });
            };
            reader.readAsDataURL(file);
          });
        })
      ).then(newPhotos => {
        setTextboxPhotos(prev => {
          const existingPhotos = prev[focusedTextboxId] || [];
          return {
            ...prev,
            [focusedTextboxId]: [...existingPhotos, ...newPhotos]
          };
        });
        console.log(`✅ Added ${newPhotos.length} photo(s) after ${focusedTextboxId}`);
      });
    };
    
    input.click();
  };
  
  // Handle photo operations
  const handlePhotoDelete = (textboxId, photoId) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).filter(p => p.id !== photoId)
    }));
  };
  
  const handlePhotoScale = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => ({
      ...prev,
      [textboxId]: (prev[textboxId] || []).map(p => 
        p.id === photoId 
          ? { ...p, scale: Math.max(30, Math.min(75, p.scale + (direction * 10))) }
          : p
      )
    }));
  };
  
  const handlePhotoMove = (textboxId, photoId, direction) => {
    setTextboxPhotos(prev => {
      const photos = prev[textboxId] || [];
      const index = photos.findIndex(p => p.id === photoId);
      if (index === -1) return prev;
      
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= photos.length) return prev;
      
      const newPhotos = [...photos];
      [newPhotos[index], newPhotos[newIndex]] = [newPhotos[newIndex], newPhotos[index]];
      
      return {
        ...prev,
        [textboxId]: newPhotos
      };
    });
  };
  
  // Handle photo height update
  const handlePhotoHeightUpdate = (textboxId, photoId, height) => {
    setPhotoHeights(prev => ({
      ...prev,
      [`${textboxId}_${photoId}`]: height
    }));
  };
  
  // Save userTextboxes and photos to reportSections whenever they change
  useEffect(() => {
    updateReportSection('nextPhase', 'userTextboxes', userTextboxes);
  }, [userTextboxes]);
  
  useEffect(() => {
    updateReportSection('nextPhase', 'textboxPhotos', textboxPhotos);
  }, [textboxPhotos]);

  // Recalculate pages whenever section heights, user textboxes, or photos change
  useEffect(() => {
    const newLayout = calculatePages();
    setPageLayout(newLayout);
  }, [sectionHeights, userTextboxes, textboxPhotos, photoHeights]);

  // Register add/delete/photo handlers with parent
  useEffect(() => {
    if (textboxActionsRef) {
      textboxActionsRef.current = {
        onAdd: handleAddTextbox,
        onDelete: handleDeleteTextbox,
        onAddPhotos: handleAddPhotos
      };
    }
  }, [focusedTextboxId, textboxActionsRef, userTextboxes]);
  
  // Update parent about focused textbox info
  useEffect(() => {
    if (onTextboxInfoChange) {
      const isUserTextbox = focusedTextboxId && focusedTextboxId.startsWith('userTextbox_');
      onTextboxInfoChange({
        textboxId: focusedTextboxId,
        canDelete: isUserTextbox
      });
    }
  }, [focusedTextboxId, onTextboxInfoChange]);

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #059669', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>🚀 14.0 Next Phase Planning</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#ecfdf5', 
                color: '#065f46', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#ecfdf5', 
            borderRadius: '4px', 
            border: '1px solid #86efac' 
          }}>
            <p style={{ 
              color: '#065f46', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Strategic planning for upcoming project phases, resource allocation, and future milestone preparation
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const formSections = [
    {
      id: 'scopeOfWork',
      label: 'Scope of Work & Key Activities',
      placeholder: 'Detail the major activities, deliverables, and work packages planned for the next phase. Include construction activities, installations, testing, and commissioning work...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'keyMilestones',
      label: 'Key Milestones & Critical Path Activities',
      placeholder: 'Identify major milestones, critical path activities, dependencies, and key decision points. Include completion targets and project phase gates...',
      rows: 6,
      estimatedHeight: 30
    },
    {
      id: 'riskManagement',
      label: 'Risk Management & Contingency Planning',
      placeholder: 'Identify potential risks for the next phase, mitigation strategies, contingency plans, and risk monitoring procedures...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'qualitySafety',
      label: 'Quality & Safety Planning',
      placeholder: 'Outline quality control procedures, inspection schedules, safety protocols, training requirements, and compliance measures for the next phase...',
      rows: 5,
      estimatedHeight: 30
    },
    {
      id: 'financialDetails',
      label: 'Financial Planning Details',
      placeholder: 'Detail financial planning considerations, payment schedules, funding requirements, and cost optimization strategies...',
      rows: 4,
      estimatedHeight: 30
    },
    {
      id: 'coordination',
      label: 'Coordination & Communication Plan',
      placeholder: 'Outline coordination procedures, meeting schedules, communication protocols, stakeholder management, and project team structure for the next phase...',
      rows: 4,
      estimatedHeight: 30
    }
  ];

  // Create form field component with shared toolbar integration
  const createFormField = (section, pageIndex = 0) => {
    const content = reportSections.nextPhase?.[section.id] || '';
    const isUserTextbox = section.id.startsWith('userTextbox_');
    
    // Calculate max height for this textbox
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    // Calculate space used before this textbox on current page
    const currentPageItems = pageLayout[pageIndex] || [];
    const itemIndex = currentPageItems.findIndex(item => item.type === 'textbox' && item.id === section.id);
    
    let usedHeightBefore = 0;
    if (itemIndex > 0) {
      for (let i = 0; i < itemIndex; i++) {
        const prevItem = currentPageItems[i];
        usedHeightBefore += prevItem.itemHeight || 25;
      }
    }
    
    const maxHeight = AVAILABLE_HEIGHT - usedHeightBefore - SECTION_LABEL_HEIGHT - SECTION_MARGIN;
    
    return (
      <div key={`${section.id}-${pageIndex}`} style={{ marginBottom: '5mm' }}>
        {!isUserTextbox && (
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '2mm',
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#111827', 
            marginBottom: '3mm' 
          }}>
            {section.label}
            <span 
              title={section.placeholder}
              style={{ 
                cursor: 'help',
                color: '#6b7280',
                fontSize: '12px'
              }}
            >
              ℹ️
            </span>
          </label>
        )}
        <TiptapEditor
          value={content}
          onChange={(newContent) => {
            updateReportSection('nextPhase', section.id, newContent);
          }}
          placeholder="" // Removed placeholder text
          initialHeight={section.estimatedHeight}
          onContentOverflow={handleHeightUpdate}
          onToolbarCommand={toolbarCommandRef}
          onFocusChange={(isFocused) => {
            if (onRichTextFocusChange) {
              onRichTextFocusChange(isFocused);
            }
            if (isFocused) {
              setFocusedTextboxId(section.id);
            }
          }}
          pageContext={{
            pageIndex,
            editorId: section.id,
            maxHeight: maxHeight
          }}
          style={{
            width: '190mm',
            border: '1px solid #d1d5db',
            borderRadius: '2px',
            fontSize: '12px',
            lineHeight: '1.4'
          }}
        />
      </div>
    );
  };

  const createPlanningMetrics = () => (
    <div style={{ marginBottom: '6mm' }}>
      <label style={{ 
        display: 'block', 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '3mm' 
      }}>
        Next Phase Overview
      </label>
      
      {/* Phase Overview Metrics */}
      <div style={{ marginBottom: '4mm' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr 1fr', 
          gap: '3mm',
          backgroundColor: '#ecfdf5',
          padding: '4mm',
          borderRadius: '2px',
          border: '1px solid #86efac',
          marginBottom: '4mm'
        }}>
          {[
            { id: 'phaseName', label: 'Phase Name', placeholder: '', type: 'text' },
            { id: 'startDate', label: 'Planned Start Date', placeholder: '', type: 'date' },
            { id: 'duration', label: 'Estimated Duration (Days)', placeholder: '', type: 'number' }
          ].map(field => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                color: '#065f46', 
                marginBottom: '1mm' 
              }}>
                {field.label}
              </label>
              <input
                type={field.type}
                value={reportSections.nextPhase?.[field.id] || ''}
                onChange={(e) => updateReportSection('nextPhase', field.id, e.target.value)}
                style={{
                  width: '100%',
                  padding: '2mm',
                  border: '1px solid #d1d5db',
                  borderRadius: '2px',
                  fontSize: '11px',
                  outline: 'none'
                }}
                placeholder={field.placeholder}
                onFocus={(e) => {
                  e.target.style.borderColor = '#059669';
                  e.target.style.boxShadow = '0 0 0 1px #059669';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const calculatePages = () => {
    const PAGE_HEIGHT = 277;
    const HEADER_HEIGHT = 25;
    const FOOTER_HEIGHT = 15;
    const AVAILABLE_HEIGHT = PAGE_HEIGHT - HEADER_HEIGHT - FOOTER_HEIGHT;
    const SECTION_LABEL_HEIGHT = 8;
    const SECTION_MARGIN = 5;
    
    const allItems = getAllItems();
    let pages = [];
    let currentPage = [];
    let currentPageUsedHeight = 0; // Start from 0 like proven sections
    
    // Add metrics as first item manually
    const METRICS_HEIGHT = 25; // Reduced since we removed sections
    const metricsItem = { type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT };
    if (currentPageUsedHeight + METRICS_HEIGHT <= AVAILABLE_HEIGHT) {
      currentPage.push(metricsItem);
      currentPageUsedHeight += METRICS_HEIGHT;
    }
    
    allItems.forEach((item) => {
      let itemHeight = 0;
      
      if (item.type === 'textbox') {
        const sectionHeight = sectionHeights[item.id] || item.estimatedHeight || 25;
        itemHeight = SECTION_LABEL_HEIGHT + sectionHeight + SECTION_MARGIN;
      } else if (item.type === 'photo') {
        itemHeight = photoHeights[`${item.textboxId}_${item.id}`] || 50;
      }
      
      if (currentPageUsedHeight + itemHeight <= AVAILABLE_HEIGHT) {
        currentPage.push({ ...item, itemHeight });
        currentPageUsedHeight += itemHeight;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
        }
        currentPage = [{ ...item, itemHeight }];
        currentPageUsedHeight = itemHeight;
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [[{ type: 'metrics', id: 'metrics', itemHeight: METRICS_HEIGHT }]];
  };

  const createPages = () => {
    const localTotalPages = pageLayout.length;
    
    return pageLayout.map((pageItems, pageIndex) => {
      const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('nextphase', pageIndex + 1) : pageIndex + 1;
      
      return (
      <A4Page key={`next-phase-page-${pageIndex}`} globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(pageIndex + 1, localTotalPages)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden'
          }}>
            {pageItems.map((item, index) => {
              if (item.type === 'metrics') {
                return <div key="metrics">{createPlanningMetrics()}</div>;
              } else if (item.type === 'textbox') {
                const section = item.id.startsWith('userTextbox_') 
                  ? userTextboxes.find(tb => tb.id === item.id)
                  : formSections.find(s => s.id === item.id);
                return section ? createFormField(section, pageIndex) : null;
              } else if (item.type === 'photo') {
                return (
                  <TextboxPhoto
                    key={item.id}
                    photo={item}
                    onDelete={() => handlePhotoDelete(item.textboxId, item.id)}
                    onScale={(direction) => handlePhotoScale(item.textboxId, item.id, direction)}
                    onMove={(direction) => handlePhotoMove(item.textboxId, item.id, direction)}
                    onHeightChange={(height) => {
                      setPhotoHeights(prev => ({ ...prev, [`${item.textboxId}_${item.id}`]: height }));
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
          
          {pageIndex === localTotalPages - 1 && (
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              paddingTop: '3mm', 
              textAlign: 'center', 
              fontSize: '10px', 
              color: '#6b7280',
              height: '15mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Next Phase Planning - SiteOrbit Construction Management System
            </div>
          )}
        </div>
      </A4Page>
      );
    });
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Appendices & Additional Documentation Tab with A4 formatting
export const AppendicesTab = () => {
  const { reportSections, updateReportSection, updateSectionPageCount, getGlobalPageNumber, totalPages: globalTotalPages } = useReports();

  // Report page count to parent context (always 1 page for appendices)
  useEffect(() => {
    if (updateSectionPageCount) {
      updateSectionPageCount('appendices', 1);
    }
  }, [updateSectionPageCount]);

  const createPageHeader = (localPageNumber, localTotalPages) => (
    <div style={{ marginBottom: '5mm' }}>
      <div style={{ borderBottom: '2px solid #6b7280', paddingBottom: '3mm', marginBottom: '3mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: 0 }}>📎 15.0 Appendices & Additional Documentation</h2>
          {localTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
              <span style={{ 
                fontSize: '10px', 
                backgroundColor: '#f9fafb', 
                color: '#4b5563', 
                padding: '1mm 2mm', 
                borderRadius: '10px' 
              }}>
                Page {localPageNumber} of {localTotalPages}
              </span>
            </div>
          )}
        </div>
        
        {localPageNumber === 1 && (
          <div style={{ 
            marginTop: '3mm', 
            padding: '2mm 3mm',
            backgroundColor: '#f9fafb', 
            borderRadius: '4px', 
            border: '1px solid #d1d5db' 
          }}>
            <p style={{ 
              color: '#4b5563', 
              fontSize: '13px', 
              margin: 0,
              fontWeight: '500'
            }}>
              Supporting documents, supplementary information, and additional project documentation
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Placeholder content - awaiting further instructions
  const createPages = () => {
    const globalPageNum = getGlobalPageNumber ? getGlobalPageNumber('appendices', 1) : 1;
    
    return [
      <A4Page key="appendices-page-1" globalPageNumber={globalPageNum} totalPages={globalTotalPages}>
        <div style={{ width: '190mm', height: '277mm', display: 'flex', flexDirection: 'column' }}>
          <div style={{ height: '25mm', flexShrink: 0 }}>
            {createPageHeader(1, 1)}
          </div>
          
          <div style={{ 
            flex: 1, 
            paddingBottom: '10mm',
            overflowY: 'hidden',
            padding: '10mm'
          }}>
            {/* Blank content area */}
          </div>
          
          <div style={{ 
            borderTop: '1px solid #e5e7eb', 
            paddingTop: '3mm', 
            textAlign: 'center', 
            fontSize: '10px', 
            color: '#6b7280',
            height: '15mm',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            Appendices & Additional Documentation - SiteOrbit Construction Management System
          </div>
        </div>
      </A4Page>
    ];
  };

  return (
    <div style={{ width: '210mm', margin: '0 auto' }}>
      {createPages()}
    </div>
  );
};